import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.xray');
import { ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Service } from "src/libs/portal/season/service";

type PostureFinalStatus = 'NORMAL' | 'BORDERLINE' | 'CORRECTION_REQUIRED' | 'UNAVAILABLE';
type PostureClassificationSource = 'RULE_BASED' | 'LEARNED_MODEL' | 'FALLBACK_RULE_BASED';
type PostureFinalClass =
    | 'NORMAL'
    | 'SHOULDER_ALIGNMENT_ERROR'
    | 'UPPER_BODY_CENTER_ERROR'
    | 'CAPTURE_RANGE_ERROR';
type PostureCheckResult = {
    checkId: string;
    status: PostureFinalStatus;
    reasons?: string[];
    metrics?: Record<string, unknown>;
};
type NormalizedPostureFeatures = Record<string, number | boolean | string | string[] | null> & {
    featureAvailable?: boolean;
    unavailableReasons?: string[];
};
type TemporalPostureResult = {
    method: string;
    validFrameCount: number;
    votingWindowSize: number;
    stable: boolean;
    tie: boolean;
    finalClass: PostureFinalClass | null;
    finalStatus: PostureFinalStatus;
};
type XrayAnalysisResponse = {
    finalStatus?: PostureFinalStatus | null;
    finalClass?: PostureFinalClass | null;
    classificationSource?: PostureClassificationSource | null;
    perCheckResults?: PostureCheckResult[] | null;
    normalizedFeatures?: NormalizedPostureFeatures | null;
    unavailableReasons?: string[] | null;
    correctionDirection?: string | null;
    correctionMessage?: string | null;
    validFrameCount?: number | null;
    votingWindowSize?: number | null;
    modelVersion?: string | null;
    detectorVersion?: string | null;
    poseVersion?: string | null;
    thresholdConfigVersion?: string | null;
    active_pose_model_id?: string | null;
    active_detector_model_id?: string | null;
    trained_pose_model_applied?: boolean;
    trained_detector_model_applied?: boolean;
    trained_pose_model_executed?: boolean;
    trained_detector_model_executed?: boolean;
    temporalStabilization?: TemporalPostureResult | null;
    landmarks?: Record<string, unknown>[];
    feedback?: Record<string, unknown>[];
    debug?: unknown;
    pass?: boolean;
    score?: number;
    image?: string;
    voice?: { key?: string; text?: string; audio?: string } | null;
    [key: string]: unknown;
};
type WizCallResponse = { code: number; data: unknown };
type PostureVote = { status: PostureFinalStatus; className: PostureFinalClass };
type TemporalGuideState = 'SEARCHING' | 'DETECTED' | 'CORRECTING' | 'STABLE' | 'PASS';
type FilterAxisState = {
    raw: number;
    filtered: number;
    derivative: number;
    timestamp: number;
    microDirection?: number;
    microFrames?: number;
    microMotion?: boolean;
};
type TemporalPointState = {
    x: FilterAxisState;
    y: FilterAxisState;
    lastVisibleAt: number;
    confidence: number;
};
type DetectorLockState = 'searching' | 'verifying' | 'locked';
type DetectorCandidate = {
    detection: any;
    corners: any[];
    center: any;
    area: number;
    observedAt: number;
};

const POSTURE_FINAL_STATUSES: PostureFinalStatus[] = [
    'NORMAL', 'BORDERLINE', 'CORRECTION_REQUIRED', 'UNAVAILABLE'
];
const POSTURE_CLASSIFICATION_SOURCES: PostureClassificationSource[] = [
    'RULE_BASED', 'LEARNED_MODEL', 'FALLBACK_RULE_BASED'
];
const POSTURE_FINAL_CLASSES: PostureFinalClass[] = [
    'NORMAL', 'SHOULDER_ALIGNMENT_ERROR', 'UPPER_BODY_CENTER_ERROR', 'CAPTURE_RANGE_ERROR'
];
// MediaPipe keeps its 33 landmark indices, but the live X-ray guide uses the
// shoulders, elbows, and torso base only. Torso points are drawn when available;
// only shoulders and elbows gate inference so a cropped hip cannot stall it.
const XRAY_UPPER_BODY_CONNECTIONS: number[][] = [
    [11, 12], [11, 13], [12, 14],
    [11, 23], [12, 24], [23, 24],
];
const XRAY_POSTURE_KEY_IDS: number[] = [11, 12, 13, 14, 23, 24];
const XRAY_PRIMARY_GATE_IDS: number[] = [11, 12, 13, 14];
const XRAY_SHOULDER_IDS: number[] = [11, 12];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isWizCallResponse(value: unknown): value is WizCallResponse {
    return isRecord(value) && typeof value['code'] === 'number' && 'data' in value;
}

function isOptionalString(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'string';
}

function isOptionalNumber(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'number';
}

function isPostureCheckResult(value: unknown): value is PostureCheckResult {
    if (!isRecord(value) || typeof value['checkId'] !== 'string') return false;
    if (!POSTURE_FINAL_STATUSES.includes(value['status'] as PostureFinalStatus)) return false;
    const reasons = value['reasons'];
    if (reasons !== undefined && (!Array.isArray(reasons)
        || reasons.some((reason) => typeof reason !== 'string'))) return false;
    const metrics = value['metrics'];
    // Geometry details such as chestRoi are nested objects. They are valid
    // JSON metrics and must not make a successful analysis response fail the
    // browser-side contract check.
    if (metrics !== undefined && !isRecord(metrics)) return false;
    return true;
}

function isXrayAnalysisResponse(value: unknown): value is XrayAnalysisResponse {
    if (!isRecord(value)) return false;
    const finalStatus = value['finalStatus'];
    const finalClass = value['finalClass'];
    const classificationSource = value['classificationSource'];
    const perCheckResults = value['perCheckResults'];
    const normalizedFeatures = value['normalizedFeatures'];
    const unavailableReasons = value['unavailableReasons'];
    if (finalStatus !== undefined && finalStatus !== null
        && !POSTURE_FINAL_STATUSES.includes(finalStatus as PostureFinalStatus)) return false;
    if (finalClass !== undefined && finalClass !== null
        && !POSTURE_FINAL_CLASSES.includes(finalClass as PostureFinalClass)) return false;
    if (classificationSource !== undefined && classificationSource !== null
        && !POSTURE_CLASSIFICATION_SOURCES.includes(classificationSource as PostureClassificationSource)) return false;
    if (perCheckResults !== undefined && perCheckResults !== null
        && (!Array.isArray(perCheckResults)
            || perCheckResults.some((item) => !isPostureCheckResult(item)))) return false;
    if (normalizedFeatures !== undefined && normalizedFeatures !== null
        && !isRecord(normalizedFeatures)) return false;
    if (unavailableReasons !== undefined && unavailableReasons !== null
        && (!Array.isArray(unavailableReasons)
            || unavailableReasons.some((reason) => typeof reason !== 'string'))) return false;
    if (!isOptionalString(value['correctionDirection'])
        || !isOptionalString(value['correctionMessage'])
        || !isOptionalString(value['modelVersion'])
        || !isOptionalString(value['detectorVersion'])
        || !isOptionalString(value['poseVersion'])
        || !isOptionalString(value['thresholdConfigVersion'])) return false;
    if (!isOptionalNumber(value['validFrameCount'])
        || !isOptionalNumber(value['votingWindowSize'])) return false;
    return true;
}

@Component({ standalone: false,
    selector: 'wiz-page-xray',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.xray/view.scss */
:host {
  display: block;
  height: 100vh;
  overflow: hidden;
}

.xray-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
}

.xray-header {
  flex: 0 0 auto;
}

.xray-main {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.xray-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  gap: 12px;
  height: 100%;
  min-height: 0;
  align-items: stretch;
}

.xray-camera-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.xray-viewport {
  flex: 1 1 auto;
  min-height: 0;
  background: #000;
  overflow: hidden;
}

.xray-judge-panel {
  min-height: 0;
  overflow-y: auto;
}

.xray-viewport > video,
.xray-viewport > #xray-overlay,
.xray-viewport > img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

@supports not (aspect-ratio: 16/9) {
  .xray-viewport::before {
    content: "";
    display: block;
    padding-top: 56.25%;
  }
}
@media (max-width: 980px) {
  :host {
    height: auto;
    overflow: visible;
  }
  .xray-page {
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }
  .xray-main {
    overflow: visible;
  }
  .xray-workspace {
    grid-template-columns: 1fr;
    height: auto;
  }
  .xray-viewport {
    aspect-ratio: 16/9;
    min-height: 220px;
  }
  .xray-judge-panel {
    overflow: visible;
  }
}`],
})
export class PageXrayComponent implements OnInit, OnDestroy {
    public status: any = {};
    public analysis: any = null;
    public resultImage: string = '';
    public message: string = '';
    public busy: boolean = false;
    public cameraOn: boolean = false;
    public realtime: boolean = false;
    public videoUploadMode: boolean = false;
    public videoGuideRunning: boolean = false;
    public cameraError: string = '';
    public facingMode: 'environment' | 'user' = 'environment';
    public landmarkCount: number = 0;
    public debugInfo: any = null;
    public primaryOkCount: number = 0;
    public primaryTotalCount: number = 0;
    public secondaryIssueCount: number = 0;
    public sourceLabel: string = '대기 중';
    public previewImageSrc: string = '';
    public displayStatus: string = '대기 중';
    public voiceEnabled: boolean = false;
    public voiceBusy: boolean = false;
    public voiceMessage: string = '';
    public voiceError: string = '';
    public normalSkeletonEnabled: boolean = false;
    public calibrationState: 'idle' | 'calibrating' | 'ready' = 'idle';
    public calibrationProgress: number = 0;
    public adaptiveMessage: string = '';
    public detectorCalibrationMode: boolean = false;
    public detectorCorners: any[] = [];
    public detectorMessage: string = '실시간 카메라에서 학습 디텍터가 촬영판을 자동 인식합니다.';
    public detectorCalibrated: boolean = false;
    public detectorLockState: DetectorLockState = 'searching';
    public detectorLockSampleCount: number = 0;
    public buckyDetection: any = null;
    public buckyModelStatus: any = {};
    public postureModelStatus: any = {};
    public modelStatusState: 'loading' | 'ready' | 'unavailable' = 'loading';
    public modelDetailsOpen: boolean = false;
    public armOverlayDebugRows: any[] = [];
    public detailAnalysisOpen: boolean = false;
    public userSummaryMessage: string = '측정을 시작하면 자세 안내가 표시됩니다.';
    public primaryActionItems: any[] = [];
    public researchMode: boolean = false;
    public researchPipelineStatus: any = {};
    public readonly detectorCornerLabels: string[] = ['좌상단', '우상단', '우하단', '좌하단'];
    public temporalGuideState: TemporalGuideState = 'SEARCHING';
    public temporalMotion: any = null;
    public temporalDebug: any = {
        source_fps: 0,
        pose_fps: 0,
        skeleton_render_fps: 0,
        posture_inference_fps: 0,
        inference_latency_ms: 0,
        overlay_age_ms: 0,
        tracking_confidence: 0,
        core_tracking_confidence: 0,
        core_pose_ready: false,
        held_core_joints: 0,
        dropped_frames: 0,
        skeleton_jump_count: 0,
        posture_flicker_count: 0,
        bucky_age_ms: null,
        pose_backend: 'initializing',
    };

    private stream: MediaStream | null = null;
    private realtimeTimer: any = null;
    private captureBusy: boolean = false;
    private overlayAnimationFrame: number | null = null;
    private overlayStartLandmarks: any[] = [];
    private overlayTargetLandmarks: any[] = [];
    private overlayRenderedLandmarks: any[] = [];
    private overlayStartDetectorCorners: any[] = [];
    private overlayTargetDetectorCorners: any[] = [];
    private overlayRenderedDetectorCorners: any[] = [];
    private overlayStartDetectorCenter: any = null;
    private overlayTargetDetectorCenter: any = null;
    private overlayRenderedDetectorCenter: any = null;
    private overlayTransitionStartedAt: number = 0;
    private overlayTransitionDurationMs: number = 120;
    private overlayLastTargetAt: number = 0;
    private overlayPassed: boolean = false;
    private readonly feedbackRealtimeDelayMs: number = 5000;
    private readonly calibrationDurationMs: number = 3000;
    private readonly minCalibrationSamples: number = 3;
    private readonly captureMaxWidth: number = 640;
    private trackingSessionId: string = this.createTrackingSessionId();
    private previewImageWidth: number = 0;
    private previewImageHeight: number = 0;
    private uploadObjectUrl: string = '';
    private lastVoiceKey: string = '';
    private lastVoiceAt: number = 0;
    private voiceAudio: HTMLAudioElement | null = null;
    private voiceCache: Map<string, string> = new Map();
    private readonly silentAudioData: string = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQQAAAAAAA==';
    private readonly voiceRepeatDelayMs: number = 4500;
    private readonly postureVotingWindowSize: number = 5;
    private readonly postureMinimumValidFrames: number = 3;
    private postureVoteHistory: PostureVote[] = [];
    private voicePlayed: boolean = false;
    private lastAdaptiveVoiceKey: string = '';
    private lastAdaptiveVoiceAt: number = 0;
    private lastAdaptiveVoiceWasPass: boolean = false;
    private lastBrowserSpeechText: string = '';
    private lastBrowserSpeechAt: number = 0;
    private audioContext: AudioContext | null = null;
    private voiceUnlocked: boolean = false;
    private voiceAudioUnlocked: boolean = false;
    private calibrationStartedAt: number = 0;
    private calibrationSamples: any[][] = [];
    private personalizedSkeleton: any[] = [];
    private pendingUploadFile: File | null = null;
    private uploadedVideoFile: File | null = null;
    private uploadedVideoSha256: string = '';
    private reviewedVideoPoseCorrections: any[] = [];
    private poseCorrectionLoadGeneration: number = 0;
    private detectorCornerSaving: boolean = false;
    private lastDetectorCornerClickAt: number = 0;
    private uploadRequestGeneration: number = 0;
    private readonly resizeHandler = () => {
        requestAnimationFrame(() => this.drawCurrentOverlayFrame());
    };
    private temporalPoseWorker: Worker | null = null;
    private temporalWorkerReady: boolean = false;
    private temporalWorkerReadyPromise: Promise<void> | null = null;
    private temporalPoseCanvas: HTMLCanvasElement = document.createElement('canvas');
    private temporalPoseContext: CanvasRenderingContext2D | null = null;
    private temporalHardwareAcceleration: boolean = false;
    private temporalPoseGeneration: number = 0;
    private temporalFrameHandle: number | null = null;
    private temporalFrameUsesVideoCallback: boolean = false;
    private temporalPoseBusy: boolean = false;
    private latestTemporalLandmarks: any[] = [];
    private previousTemporalLandmarks: any[] = [];
    private temporalPointStates: Map<number, TemporalPointState> = new Map();
    private temporalRuntimeStartedAt: number = 0;
    private lastTemporalPoseAt: number = 0;
    private lastTemporalRenderAt: number = 0;
    private lastMediaTime: number = -1;
    private lastPoseTimestampMs: number = 0;
    private lastPoseAttemptAt: number = 0;
    private temporalDetectedFrames: number = 0;
    private temporalStableSince: number = 0;
    private postureTimer: any = null;
    private postureLoopGeneration: number = 0;
    private lastPostureRequestAt: number = 0;
    private postureRetryAfter: number = 0;
    private postureFailureCount: number = 0;
    private lastBuckyDetectionAt: number = 0;
    private lastBuckyDetectionAttemptAt: number = 0;
    private detectorCalibrationRevision: number = 0;
    private detectorValidationUntil: number = 0;
    private detectorLockCandidates: DetectorCandidate[] = [];
    private detectorSearchFailureCount: number = 0;
    private lastDetectorFailureData: XrayAnalysisResponse | null = null;
    private temporalMetricsWindowStartedAt: number = 0;
    private temporalSourceFrames: number = 0;
    private temporalPoseFrames: number = 0;
    private temporalRenderFrames: number = 0;
    private temporalPostureResponses: number = 0;
    private temporalDroppedFrames: number = 0;
    private temporalSkeletonJumpCount: number = 0;
    private temporalPostureFlickerCount: number = 0;
    private temporalLastPosturePass: boolean | null = null;
    private temporalLastPostureChangeAt: number = 0;
    private readonly postureInferenceIntervalMs: number = 500;
    private readonly buckyFailureRetryIntervalMs: number = 10000;
    private readonly detectorValidationWindowMs: number = 5000;
    private readonly detectorLockRequiredFrames: number = 3;
    private readonly detectorInitialAcquisitionAttempts: number = 4;
    private readonly detectorCandidateMaxCornerDrift: number = 0.045;
    private readonly detectorCandidateMemoryMs: number = 6000;
    private readonly lowConfidenceHoldMs: number = 320;
    private readonly shoulderLowConfidenceHoldMs: number = 900;
    private readonly coreConfidenceMin: number = 0.3;
    private readonly shoulderTrackingConfidenceMin: number = 0.42;
    private readonly temporalServerFallbackDelayMs: number = 1800;
    // A cold MediaPipe start downloads and compiles the WASM/model. Eight seconds
    // was shorter than normal on restricted iframes and caused a healthy worker
    // to be killed while it was still compiling.
    private readonly temporalWorkerInitTimeoutMs: number = 30000;
    private readonly temporalStaticDeadband: number = 0.0025;
    private readonly shoulderStaticDeadband: number = 0.0035;
    private readonly temporalMicroMotionReleaseFrames: number = 3;
    private readonly overlayStaticMedianDeadband: number = 0.003;
    private readonly overlayStaticMaxDeadband: number = 0.008;
    private readonly temporalStableDurationMs: number = 1500;

    constructor(@Inject( Service)         public service: Service,@Inject( ChangeDetectorRef)         public ref: ChangeDetectorRef,@Inject( Router)         public router: Router,@Inject( TranslateService    )         public translate: TranslateService    ) { }

    public async ngOnInit() {
        await this.service.init(this);
        window.addEventListener('resize', this.resizeHandler);
        void this.refreshStatus();
    }

    public ngOnDestroy() {
        this.stopRealtime();
        this.stopCamera();
        this.stopVoiceAudio();
        this.temporalPoseWorker?.terminate();
        this.temporalPoseWorker = null;
        window.removeEventListener('resize', this.resizeHandler);
        this.resetUploadedVideoElement();
        this.revokeUploadObjectUrl();
    }

    public go(path: string) {
        this.service.href(path);
    }

    public detectorTerminology(value: any): string {
        return String(value || '')
            .replace(/흉부\s*버키/g, '디텍터')
            .replace(/비버키/g, '디텍터가 없는')
            .replace(/버키/g, '디텍터')
            .replace(/\bDetector\b/g, '디텍터');
    }

    public shortHash(value: any): string {
        const text = String(value || '');
        return text ? text.slice(0, 12) : '-';
    }

    public get currentPostureModeLabel(): string {
        const mode = String(
            this.analysis?.effective_inference_mode
            || this.analysis?.inference_mode
            || this.postureModelStatus?.effective_mode
            || this.postureModelStatus?.requested_mode
            || ''
        ).toLowerCase();
        const labels: Record<string, string> = {
            trained: '학습 자세모델 단독',
            hybrid: '학습 자세모델 + 안전 규칙',
            shadow: '학습모델 비교 · 규칙 판정',
            legacy: '규칙 기반 판정',
        };
        return labels[mode] || (this.modelStatusState === 'loading' ? '확인 중' : '상태 확인 필요');
    }

    public get currentFrameUsesTrainedModels(): boolean {
        return Boolean(
            this.analysis?.trained_pose_model_applied
            && this.analysis?.trained_detector_model_applied
        );
    }

    public get latestTemporalLandmarksAvailable(): boolean {
        return this.latestTemporalLandmarks.length === 33;
    }

    public async refreshStatus() {
        this.modelStatusState = 'loading';
        try {
            const { code, data } = await wiz.call('status_v2');
            if (code === 200) {
                this.status = data;
                this.buckyModelStatus = data?.trained_yolo_camera || {};
                this.postureModelStatus = data?.posture_candidate || {};
                this.modelStatusState = this.buckyModelStatus?.ready
                    && this.postureModelStatus?.ready
                    ? 'ready'
                    : 'unavailable';
                this.applyDetectorStatus(data);
            } else {
                this.modelStatusState = 'unavailable';
            }
        } catch (_error) {
            this.modelStatusState = 'unavailable';
        }
        await this.service.render();
    }

    public async runSampleTest() {
        this.busy = true;
        this.message = '샘플 이미지를 분석하는 중입니다.';
        await this.service.render();
        const { code, data } = await wiz.call('sample_test');
        this.message = code === 200 ? `검출률 ${data.detection_rate}% (${data.detected}/${data.total})` : '샘플 검출에 실패했습니다.';
        this.busy = false;
        await this.refreshStatus();
    }

    public async toggleCamera() {
        if (this.researchMode) {
            this.message = 'AlphaPose + InceptionV3 연구 모드는 사진 업로드 1회 분석만 지원합니다.';
            await this.service.render();
            return;
        }
        this.uploadRequestGeneration += 1;
        if (this.cameraOn) {
            this.stopRealtime();
            this.stopCamera();
        } else {
            this.stopRealtime();
            this.resetUploadedVideoElement();
            this.revokeUploadObjectUrl();
            this.pendingUploadFile = null;
            this.resetRealtimeDetectorForNewSource(
                '새 카메라 화면에서 학습 Detector로 촬영판을 자동 탐색합니다.',
            );
            await this.startCamera();
            if (this.cameraOn) {
                this.startRealtime();
            }
        }
        await this.service.render();
    }

    public async toggleVoice() {
        this.voiceEnabled = !this.voiceEnabled;
        this.voiceError = '';
        if (!this.voiceEnabled) {
            this.stopVoiceAudio();
            this.voiceBusy = false;
            this.voiceMessage = '';
            this.voicePlayed = false;
            this.resetAdaptiveVoiceState();
        } else {
            this.unlockVoiceOutput();
            if (this.analysis?.voice?.text && this.calibrationState !== 'calibrating') {
                this.playVoice(this.analysis, { adaptive: this.realtime && this.calibrationState === 'ready' });
            }
        }
        await this.service.render();
    }

    public async toggleNormalSkeleton() {
        this.normalSkeletonEnabled = !this.normalSkeletonEnabled;
        if (this.normalSkeletonEnabled) {
            const currentLandmarks = this.hasCalibrationJoints(this.latestTemporalLandmarks)
                ? this.latestTemporalLandmarks
                : (this.analysis?.landmarks || []);
            if (currentLandmarks.length) {
                this.prepareNormalReferenceSkeleton(currentLandmarks);
            } else {
                this.personalizedSkeleton = [];
                this.adaptiveMessage = '관절이 확인되는 첫 프레임에 학습된 정상 자세 형상을 생성해 고정합니다.';
            }
        } else {
            this.personalizedSkeleton = [];
            this.adaptiveMessage = '';
        }
        if (this.analysis?.landmarks?.length) {
            requestAnimationFrame(() => this.drawOverlay(this.analysis.landmarks, this.analysis.pass));
        } else {
            this.clearOverlay();
        }
        await this.service.render();
    }

    public async toggleResearchMode() {
        if (!this.researchMode) {
            if (!this.researchPipelineStatus?.ready) {
                this.cameraError = 'AlphaPose + InceptionV3 연구 아티팩트가 준비되지 않았습니다.';
                await this.service.render();
                return;
            }
            this.stopRealtime();
            this.stopCamera();
            this.resetUploadedVideoElement();
            this.researchMode = true;
            this.normalSkeletonEnabled = false;
            this.message = '연구 모드가 켜졌습니다. 사진을 업로드하면 AlphaPose(YOLOX-X) + InceptionV3로 1회 분석합니다.';
        } else {
            this.researchMode = false;
            this.normalSkeletonEnabled = false;
            this.message = '기존 운영 자세 판정으로 돌아왔습니다.';
        }
        this.cameraError = '';
        this.analysis = null;
        this.displayStatus = '대기 중';
        this.clearOverlay();
        await this.service.render();
    }

    public async startDetectorCalibration() {
        this.detectorCalibrationMode = true;
        this.detectorCorners = [];
        this.buckyDetection = null;
        this.detectorCornerSaving = false;
        this.lastDetectorCornerClickAt = 0;
        this.detectorMessage = '촬영판 내부의 작은 ㄱ자 기준 마커 좌상단을 클릭해 주세요.';
        this.clearOverlay();
        await this.service.render();
    }

    public async cancelDetectorCalibration() {
        this.detectorCalibrationMode = false;
        this.buckyDetection = null;
        this.detectorCornerSaving = false;
        this.lastDetectorCornerClickAt = 0;
        await this.refreshStatus();
        this.detectorMessage = this.detectorCalibrated ? '디텍터 ㄱ자 마커 기준 좌표계 적용 중' : '디텍터 기준 좌표계: 화면 중심 proxy 사용';
        requestAnimationFrame(() => this.drawOverlay(this.analysis?.landmarks || [], this.analysis?.pass));
        await this.service.render();
    }

    public async clearDetectorCalibration() {
        if (this.pendingUploadFile || this.previewImageSrc || this.buckyDetection) {
            this.uploadRequestGeneration += 1;
            this.pendingUploadFile = null;
            this.buckyDetection = null;
            this.previewImageSrc = '';
            this.resultImage = '';
            this.analysis = null;
            this.personalizedSkeleton = [];
            this.busy = false;
            this.sourceLabel = '대기 중';
            this.displayStatus = '대기 중';
            this.revokeUploadObjectUrl();
            this.applyDetectorStatus(this.status, true);
            this.detectorCalibrationMode = false;
            this.detectorCornerSaving = false;
            this.lastDetectorCornerClickAt = 0;
            this.detectorMessage = '사진별 디텍터 좌표를 해제하고 저장된 카메라 좌표계를 복원했습니다.';
            this.clearOverlay();
            await this.service.render();
            return;
        }
        const { code, data } = await wiz.call('status_v2', { detector_action: 'clear' });
        if (code === 200) {
            this.applyDetectorStatus(data, true);
            this.detectorCalibrationMode = false;
            this.detectorCornerSaving = false;
            this.lastDetectorCornerClickAt = 0;
            this.detectorMessage = '디텍터 보정을 초기화했습니다. 화면 중심 proxy를 사용합니다.';
        }
        requestAnimationFrame(() => this.drawOverlay(this.analysis?.landmarks || [], this.analysis?.pass));
        await this.service.render();
    }

    public async captureDetectorCorner(event: MouseEvent) {
        if (!this.detectorCalibrationMode) return;
        event.preventDefault();
        event.stopPropagation();
        if (this.detectorCornerSaving || this.detectorCorners.length >= 4) return;
        const now = Date.now();
        if (now - this.lastDetectorCornerClickAt < 180) return;
        this.lastDetectorCornerClickAt = now;

        const point = this.eventToMediaPoint(event);
        if (!point) {
            this.detectorMessage = '영상 영역 안쪽을 클릭해 주세요.';
            await this.service.render();
            return;
        }

        this.detectorCorners = [...this.detectorCorners, point].slice(0, 4);
        if (this.detectorCorners.length < 4) {
            this.detectorMessage = `작은 ㄱ자 기준 마커 ${this.detectorCornerLabels[this.detectorCorners.length]}을 화면에서 클릭해 주세요.`;
            requestAnimationFrame(() => this.drawOverlay(this.analysis?.landmarks || [], this.analysis?.pass));
            await this.service.render();
            return;
        }

        this.detectorMessage = '디텍터 ㄱ자 마커 4개가 모두 지정되었습니다. 좌표계를 저장하는 중입니다.';
        requestAnimationFrame(() => this.drawOverlay(this.analysis?.landmarks || [], this.analysis?.pass));
        await this.service.render();
        await this.waitForAnimationFrame();
        await this.saveDetectorCalibration();
    }

    public async captureOnce(silent: boolean = false, sourceLabel: string = '실시간 카메라') {
        if (this.captureBusy) return;
        const video = document.getElementById('xray-video') as HTMLVideoElement;
        const canvas = document.getElementById('xray-canvas') as HTMLCanvasElement;
        if (!video || !canvas || !video.videoWidth) {
            this.cameraError = this.videoUploadMode ? '동영상 화면이 아직 준비되지 않았습니다.' : '카메라 화면이 아직 준비되지 않았습니다.';
            await this.service.render();
            return;
        }

        this.captureBusy = true;
        let overlayLandmarks: any[] = this.latestTemporalLandmarks;
        let overlayPass = false;
        let requestedBuckyRefresh = false;
        let hadCachedBucky = false;
        const requestStartedAt = performance.now();
        const detectorRevisionAtRequest = this.detectorCalibrationRevision;
        try {
            const postureCaptureWidth = this.temporalHardwareAcceleration
                ? this.captureMaxWidth
                : 416;
            const scale = Math.min(1, postureCaptureWidth / video.videoWidth);
            canvas.width = Math.round(video.videoWidth * scale);
            canvas.height = Math.round(video.videoHeight * scale);
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(
                resolve,
                'image/jpeg',
                this.temporalHardwareAcceleration ? 0.85 : 0.72,
            ));
            if (!blob) return;
            if (!silent) {
                this.busy = true;
                await this.service.render();
            }

            const form = new FormData();
            form.append('image', blob, 'camera.jpg');
            form.append('realtime', '1');
            form.append('tracking_id', this.trackingSessionId);
            const temporalOverrideReady = this.poseIdsReady(
                this.latestTemporalLandmarks,
                XRAY_POSTURE_KEY_IDS,
                true,
            );
            if (temporalOverrideReady) {
                form.append('pose_landmarks_override', JSON.stringify(this.latestTemporalLandmarks));
            }
            const now = performance.now();
            const buckyConfig = this.cachedBuckyDetectorConfig();
            hadCachedBucky = Boolean(buckyConfig);
            // The operating Detector must execute on every posture sample.
            // A cached polygon is display fallback only; sending it back as a
            // detector_config would bypass YOLO and freeze the first frame.
            const buckyRefreshDue = true;
            requestedBuckyRefresh = buckyRefreshDue;
            if (!buckyConfig) {
                if (!this.lastBuckyDetectionAttemptAt
                    || now - this.lastBuckyDetectionAttemptAt >= this.buckyFailureRetryIntervalMs) {
                    this.detectorValidationUntil = now + this.detectorValidationWindowMs;
                }
            }
            this.lastBuckyDetectionAttemptAt = now;
            const response: unknown = await wiz.call('analyze_v2', form, {
                processData: false,
                contentType: false
            });
            if (!isWizCallResponse(response) || !isXrayAnalysisResponse(response.data)) {
                throw new Error('자세 분석 API 응답 형식이 올바르지 않습니다.');
            }
            if (detectorRevisionAtRequest !== this.detectorCalibrationRevision) {
                // A recalibration click can happen while this request is in flight.
                // Never let that stale response restore the detector that was cleared.
                this.temporalDebug.inference_latency_ms = Math.round(
                    performance.now() - requestStartedAt,
                );
                await this.service.render();
                return;
            }
            const { code, data } = response;
            if (code === 200 || code === 422 || (code === 503 && Boolean(data.landmarks?.length))) {
                const automaticDetection = data?.bucky_detection || data?.bucky;
                const preserveCachedBucky = Boolean(
                    requestedBuckyRefresh
                    && hadCachedBucky
                    && automaticDetection
                    && automaticDetection?.detected === false
                );
                if (preserveCachedBucky) {
                    // Back off after a failed low-frequency refresh and keep the
                    // last valid board/posture overlay instead of flickering.
                    this.lastBuckyDetectionAt = performance.now();
                    overlayLandmarks = this.realtimeOverlayLandmarks(data);
                    overlayPass = Boolean(this.analysis?.pass);
                    this.temporalDebug.inference_latency_ms = Math.round(performance.now() - requestStartedAt);
                    this.updateTemporalGuideState(this.corePoseReady(overlayLandmarks), overlayPass);
                    if (overlayLandmarks.length) this.queueTemporalOverlay(overlayLandmarks);
                    return;
                }
                if (requestedBuckyRefresh) {
                    const detectorLocked = this.detectorCalibrated
                        ? this.acceptTrackedDetectorUpdate(automaticDetection)
                        : this.acceptDetectorLockCandidate(automaticDetection);
                    if (!detectorLocked) {
                        this.lastDetectorFailureData = data;
                        if (automaticDetection) {
                            this.buckyDetection = automaticDetection;
                        }
                        // Detector acquisition and pose acquisition are independent.
                        // Keep the server YOLO Pose overlay alive when the browser
                        // MediaPipe worker is unavailable or still initializing.
                        overlayLandmarks = this.realtimeOverlayLandmarks(data);
                        data.landmarks = overlayLandmarks;
                        this.compactRealtimeClientData(data);
                        this.analysis = data;
                        this.landmarkCount = overlayLandmarks.length;
                        this.debugInfo = data.debug || null;
                        this.sourceLabel = sourceLabel;
                        overlayPass = false;
                        this.cameraError = '';
                        this.temporalDebug.inference_latency_ms = Math.round(
                            performance.now() - requestStartedAt,
                        );
                        this.updateTemporalGuideState(this.corePoseReady(overlayLandmarks), false);
                        if (overlayLandmarks.length) this.queueTemporalOverlay(overlayLandmarks);
                        await this.service.render();
                        return;
                    }
                    this.lastBuckyDetectionAt = performance.now();
                }
                this.applyRealtimePostureStabilization(data);
                this.compactRealtimeClientData(data);
                if (automaticDetection && !this.detectorCalibrated) {
                    this.applyBuckyDetection(automaticDetection);
                }
                // Keep one visual coordinate source while browser tracking is
                // available. Server YOLO may judge a low-confidence torso, but
                // alternating its coordinates with MediaPipe makes a still body jitter.
                overlayLandmarks = this.realtimeOverlayLandmarks(data);
                data.landmarks = overlayLandmarks;
                this.analysis = data;
                this.cameraError = '';
                this.landmarkCount = overlayLandmarks.length;
                this.debugInfo = data.debug || null;
                overlayPass = Boolean(data.pass);
                this.sourceLabel = sourceLabel;
                this.updatePrimarySummary(data);
                this.updateArmOverlayDebug(overlayLandmarks, data);
                await this.playVoice(data, { adaptive: false });
                if (silent) {
                    this.resultImage = '';
                } else {
                    this.resultImage = data.image || '';
                }
                this.temporalPostureResponses += 1;
                this.recordTemporalPostureResult(overlayPass);
                this.postureFailureCount = 0;
                this.postureRetryAfter = 0;
                this.temporalDebug.inference_latency_ms = Math.round(performance.now() - requestStartedAt);
                this.updateTemporalGuideState(true, overlayPass);
            } else {
                throw new Error(String(data?.message || `자세 분석 요청이 실패했습니다. (${code})`));
            }
        } catch (error: any) {
            const detail = error?.responseJSON?.detail || error?.responseJSON?.message || error?.statusText || '';
            this.cameraError = detail ? `서버 추론 오류: ${detail}` : '서버 추론 중 오류가 발생했습니다.';
            this.postureFailureCount += 1;
            this.postureRetryAfter = performance.now() + Math.min(
                5000,
                1000 * Math.pow(2, Math.min(2, this.postureFailureCount - 1)),
            );
            if (!this.latestTemporalLandmarks.length) {
                this.analysis = {
                    score: 0,
                    pass: false,
                    feedback: [{ label: '서버', message: this.cameraError }],
                    landmarks: [],
                    debug: this.debugInfo
                };
                this.updatePrimarySummary(this.analysis);
                overlayLandmarks = [];
                this.landmarkCount = 0;
            } else {
                // A slow or failed posture request must never stop browser pose rendering.
                overlayLandmarks = this.latestTemporalLandmarks;
                this.landmarkCount = overlayLandmarks.length;
                this.updateTemporalGuideState(true, Boolean(this.analysis?.pass));
            }
        } finally {
            this.captureBusy = false;
            this.busy = false;
        }

        await this.service.render();
        if (silent) {
            this.queueSmoothOverlay(overlayLandmarks, overlayPass);
        } else {
            this.clearOverlay();
        }
    }

    public async analyzeUploadedImage(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) return;
        const requestGeneration = ++this.uploadRequestGeneration;

        this.stopRealtime();
        this.stopCamera();
        this.resetUploadedVideoElement();
        this.revokeUploadObjectUrl();
        this.clearOverlay();

        const objectUrl = URL.createObjectURL(file);
        this.pendingUploadFile = file;
        this.uploadObjectUrl = objectUrl;
        this.previewImageSrc = objectUrl;
        this.resultImage = '';
        this.sourceLabel = '업로드 이미지 · 자동 자세 판정';
        this.displayStatus = '자세 분석 중';
        this.cameraError = '';
        this.message = 'X-ray 촬영 자세를 분석하고 있습니다.';
        this.busy = true;
        this.analysis = null;
        this.voicePlayed = false;
        this.resetAdaptiveVoiceState();
        this.voiceMessage = '';
        this.voiceError = '';
        this.landmarkCount = 0;
        this.primaryOkCount = 0;
        this.primaryTotalCount = 0;
        this.secondaryIssueCount = 0;
        this.armOverlayDebugRows = [];
        this.detailAnalysisOpen = false;
        this.personalizedSkeleton = [];
        this.calibrationState = 'idle';
        this.calibrationProgress = 0;
        this.adaptiveMessage = '';
        this.detectorCalibrationMode = false;
        this.detectorCalibrated = false;
        this.detectorCorners = [];
        this.buckyDetection = null;
        this.detectorCornerSaving = false;
        this.lastDetectorCornerClickAt = 0;
        this.detectorMessage = '학습한 YOLO Bucky 모델이 사각형과 중심을 찾는 중입니다.';
        if (input) input.value = '';
        const previewSize = this.loadPreviewImageSize(objectUrl);
        await this.service.render();
        await this.waitForAnimationFrame();
        await this.waitForAnimationFrame();
        if (requestGeneration !== this.uploadRequestGeneration) return;
        const analysis = this.analyzePendingUpload(true, requestGeneration);
        await Promise.all([previewSize, analysis]);
        if (requestGeneration !== this.uploadRequestGeneration) return;
        requestAnimationFrame(() => this.drawOverlay(
            this.analysis?.landmarks || [],
            Boolean(this.analysis?.pass)
        ));
    }

    public async analyzeUploadedVideo(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) return;
        if (this.researchMode) {
            this.message = '연구 모델은 동영상·실시간 반복 추론을 지원하지 않습니다. 사진을 업로드해 주세요.';
            if (input) input.value = '';
            await this.service.render();
            return;
        }
        this.uploadRequestGeneration += 1;

        this.stopRealtime();
        this.stopCamera();
        this.resetUploadedVideoElement();
        this.revokeUploadObjectUrl();
        this.clearOverlay();

        const video = document.getElementById('xray-video') as HTMLVideoElement;
        const objectUrl = URL.createObjectURL(file);
        this.uploadedVideoFile = file;
        const correctionGeneration = ++this.poseCorrectionLoadGeneration;
        this.uploadedVideoSha256 = '';
        this.reviewedVideoPoseCorrections = [];
        void this.loadReviewedVideoPoseCorrections(file, correctionGeneration);
        this.pendingUploadFile = null;
        this.uploadObjectUrl = objectUrl;
        this.videoUploadMode = true;
        this.videoGuideRunning = false;
        this.previewImageSrc = '';
        this.resultImage = '';
        this.sourceLabel = '업로드 동영상';
        this.displayStatus = '대기 중';
        this.cameraError = '';
        this.message = '동영상을 재생하며 실제 촬영처럼 지속적으로 자세를 판단합니다.';
        this.busy = false;
        this.analysis = null;
        this.voicePlayed = false;
        this.resetAdaptiveVoiceState();
        this.voiceMessage = '';
        this.voiceError = '';
        this.landmarkCount = 0;
        this.primaryOkCount = 0;
        this.primaryTotalCount = 0;
        this.secondaryIssueCount = 0;
        this.armOverlayDebugRows = [];
        this.detailAnalysisOpen = false;
        this.personalizedSkeleton = [];
        this.detectorCalibrationMode = false;
        this.resetRealtimeDetectorForNewSource(
            '업로드 동영상의 촬영판을 학습 Detector로 최초 인식하는 중입니다.',
        );
        this.detectorCornerSaving = false;
        this.calibrationState = 'idle';
        this.calibrationProgress = 0;
        this.adaptiveMessage = '';
        await this.service.render();
        this.startUploadedVideoGuide();

        if (video) {
            video.srcObject = null;
            video.onloadeddata = async () => {
                if (!this.videoUploadMode || video.readyState < 2) return;
                this.message = '브라우저 관절 추적을 시작하고 촬영판을 최초 1회 인식합니다.';
                await this.service.render();
            };
            video.src = objectUrl;
            video.muted = true;
            video.loop = false;
            video.playsInline = true;
            video.onplay = async () => {
                if (!this.videoUploadMode || video.ended) return;
                if (!this.videoGuideRunning) this.startUploadedVideoGuide();
                this.displayStatus = '분석 중';
                this.message = '동영상을 재생하며 실제 촬영처럼 지속적으로 자세를 판단합니다.';
                await this.service.render();
            };
            video.onpause = async () => {
                if (!this.videoUploadMode || video.ended) return;
                this.stopRealtime(true);
                this.displayStatus = '일시 정지';
                this.message = '동영상이 일시 정지되어 자세 분석도 멈췄습니다.';
                await this.service.render();
            };
            video.onended = async () => {
                this.stopRealtime(true);
                if (!this.detectorCalibrated) {
                    const unavailable = this.lastDetectorFailureData || {
                        status: 'BUCKY_NOT_DETECTED',
                        finalStatus: 'UNAVAILABLE',
                        score: 0,
                        pass: false,
                        landmarks: this.latestTemporalLandmarks,
                        feedback: [],
                    };
                    this.analysis = unavailable as XrayAnalysisResponse;
                    this.updatePrimarySummary(unavailable as XrayAnalysisResponse);
                    this.displayStatus = '판정 불가';
                    this.detectorMessage = this.detectorRecoveryMessage(
                        (unavailable as any)?.reason
                        || (unavailable as any)?.bucky_detection?.review_reason,
                    );
                    this.message = '동영상 분석은 끝났지만 촬영판 위치를 확정하지 못했습니다.';
                } else {
                    this.displayStatus = this.analysis ? '분석 완료' : '판정 불가';
                    this.message = '동영상 분석이 끝났습니다.';
                }
                await this.service.render();
            };
            try {
                await video.play();
            } catch {
                this.message = '브라우저가 자동 재생을 차단했습니다. 동영상 재생 버튼을 누르면 자세 가이드가 시작됩니다.';
            }
        }

        if (input) input.value = '';
    }

    public async stopUploadedVideoGuide() {
        this.uploadRequestGeneration += 1;
        this.stopRealtime();
        this.videoGuideRunning = false;
        this.videoUploadMode = false;
        this.uploadedVideoFile = null;
        this.resetUploadedVideoElement();
        this.revokeUploadObjectUrl();
        this.message = '동영상 가이드를 중지했습니다.';
        this.sourceLabel = '대기 중';
        this.displayStatus = '대기 중';
        this.clearOverlay();
        await this.service.render();
    }

    private async analyzePendingUpload(
        autoDetectBucky: boolean = true,
        requestGeneration: number = this.uploadRequestGeneration
    ) {
        const file = this.pendingUploadFile;
        if (!file) return;
        if (!autoDetectBucky && (!this.detectorCalibrated || this.detectorCorners.length !== 4)) {
            this.cameraError = '디텍터 ㄱ자 마커 네 꼭짓점 보정이 완료된 뒤 분석할 수 있습니다.';
            await this.service.render();
            return;
        }

        this.sourceLabel = '업로드 이미지';
        this.displayStatus = '분석 중';
        this.cameraError = '';
        this.message = this.researchMode
            ? 'AlphaPose(YOLOX-X)로 관절을 찾고 InceptionV3로 분류하는 중입니다.'
            : autoDetectBucky
                ? '학습한 YOLO Pose와 YOLO Bucky로 분석하는 중입니다.'
                : '수동 디텍터 좌표로 업로드 이미지를 분석하는 중입니다.';
        this.busy = true;
        await this.service.render();

        let overlayLandmarks: any[] = [];
        let overlayPass = false;
        try {
            const form = new FormData();
            form.append('image', file, file.name || 'upload.jpg');
            form.append('require_detector', '1');
            if (!this.researchMode) {
                form.append('analysis_backend', 'yolo_candidate');
            }
            if (autoDetectBucky) {
                form.append('auto_bucky', '1');
            } else {
                const [topLeft, topRight, bottomRight, bottomLeft] = this.detectorCorners;
                form.append('detector_config', JSON.stringify({
                    version: 2,
                    source: 'manual_request_override',
                    corners: {
                        top_left: topLeft,
                        top_right: topRight,
                        bottom_right: bottomRight,
                        bottom_left: bottomLeft
                    }
                }));
            }
            const endpoint = this.researchMode ? 'analyze_research_v2' : 'analyze_v2';
            const { code, data } = await wiz.call(endpoint, form, {
                processData: false,
                contentType: false
            });
            if (requestGeneration !== this.uploadRequestGeneration) return;
            if (code === 428 || data?.detector_required) {
                this.analysis = data;
                this.buckyDetection = data?.bucky_detection || data?.bucky || null;
                this.detectorCalibrationMode = false;
                this.detectorCalibrated = false;
                this.detectorCorners = [];
                overlayLandmarks = data?.landmarks || [];
                this.landmarkCount = overlayLandmarks.length;
                this.displayStatus = '판정 불가';
                this.cameraError = '';
                this.message = data?.message
                    ? this.detectorTerminology(data.message)
                    : '촬영판을 인식하지 못해 자세를 판정할 수 없습니다. 다른 사진으로 다시 시도해 주세요.';
                this.detectorMessage = '촬영판을 인식하지 못했습니다.';
                this.updatePrimarySummary(data);
                const upstream = data?.upstream_candidate || {};
                if (upstream?.pose_model_id || upstream?.detector_model_id) {
                    this.sourceLabel = '업로드 이미지 · 자동 자세 판정';
                }
            } else if (
                (this.researchMode && code === 200 && data?.ready && data?.score_valid)
                || (!this.researchMode && (code === 200 || code === 422))
            ) {
                this.analysis = data;
                const automaticDetection = data?.bucky_detection || data?.bucky;
                if (automaticDetection) this.applyBuckyDetection(automaticDetection);
                else this.applyDetectorStatus(data, true);
                overlayLandmarks = data.landmarks || [];
                this.landmarkCount = overlayLandmarks.length;
                this.debugInfo = data.debug || null;
                overlayPass = data.pass;
                this.updatePrimarySummary(data);
                const upstream = data?.upstream_candidate || {};
                if (upstream?.pose_model_id || upstream?.detector_model_id) {
                    this.sourceLabel = '업로드 이미지 · 자동 자세 판정';
                }
                if (!this.researchMode) {
                    this.prepareNormalReferenceSkeleton(overlayLandmarks);
                    this.updateArmOverlayDebug(overlayLandmarks, data);
                }
                if (!this.researchMode) this.playVoice(data, { adaptive: false });
                this.message = '';
            } else {
                this.cameraError = data?.message
                    ? this.detectorTerminology(data.message)
                    : '이미지 분석을 진행할 수 없습니다.';
                this.message = this.cameraError;
                this.displayStatus = '분석 실패';
                if (this.researchMode) this.analysis = null;
            }
        } catch (error: any) {
            if (requestGeneration !== this.uploadRequestGeneration) return;
            const detail = error?.responseJSON?.detail || error?.responseJSON?.message || error?.statusText || '';
            this.cameraError = detail ? `이미지 분석 오류: ${detail}` : '이미지 분석 중 오류가 발생했습니다.';
            this.displayStatus = '분석 실패';
            this.analysis = {
                score: 0,
                pass: false,
                feedback: [{ label: '분석', message: this.cameraError }],
                landmarks: [],
                debug: this.debugInfo
            };
            this.updatePrimarySummary(this.analysis);
        } finally {
            if (requestGeneration === this.uploadRequestGeneration) this.busy = false;
        }

        if (requestGeneration !== this.uploadRequestGeneration) return;
        await this.service.render();
        requestAnimationFrame(() => this.drawOverlay(overlayLandmarks, overlayPass));
    }

    private async startCamera() {
        const video = document.getElementById('xray-video') as HTMLVideoElement;
        this.cameraError = '';
        this.resultImage = '';

        if (!navigator.mediaDevices?.getUserMedia) {
            this.cameraError = '브라우저에서 카메라 API를 사용할 수 없습니다. HTTPS 환경 또는 localhost에서 접속해야 합니다.';
            return;
        }

        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: this.facingMode }, width: { ideal: 960 }, height: { ideal: 540 } },
                audio: false
            });
        } catch (error) {
            try {
                this.stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            } catch (fallbackError) {
                this.cameraError = '카메라를 열 수 없습니다. 브라우저 권한, HTTPS 접속, 다른 앱의 카메라 점유 여부를 확인하세요.';
                return;
            }
        }

        video.srcObject = this.stream;
        await video.play();
        this.cameraOn = true;
    }

    private stopCamera() {
        if (this.stream) {
            this.stream.getTracks().forEach((track) => track.stop());
            this.stream = null;
        }
        this.cameraOn = false;
        this.cameraError = '';
        this.landmarkCount = 0;
        if (!this.previewImageSrc && !this.resultImage) this.clearOverlay();
    }

    private startRealtime() {
        if (this.realtime) return;
        this.videoGuideRunning = false;
        this.realtime = true;
        this.resultImage = '';
        this.revokeUploadObjectUrl();
        this.previewImageSrc = '';
        this.voicePlayed = false;
        this.resetAdaptiveVoiceState();
        this.voiceMessage = '';
        this.voiceError = '';
        this.sourceLabel = '실시간 카메라';
        this.displayStatus = '분석 중';
        this.calibrationState = 'idle';
        this.personalizedSkeleton = [];
        this.clearOverlay();
        const video = document.getElementById('xray-video') as HTMLVideoElement;
        void this.startTemporalPose(video);
        this.startPostureInferenceLoop('실시간 카메라');
    }

    private startUploadedVideoGuide() {
        if (this.videoGuideRunning) return;
        const video = document.getElementById('xray-video') as HTMLVideoElement;
        if (!video) return;

        this.videoGuideRunning = true;
        this.realtime = true;
        this.resultImage = '';
        this.previewImageSrc = '';
        this.voicePlayed = false;
        this.resetAdaptiveVoiceState();
        this.voiceMessage = '';
        this.voiceError = '';
        this.sourceLabel = '업로드 동영상';
        this.displayStatus = '분석 중';
        this.calibrationState = 'idle';
        this.personalizedSkeleton = [];
        this.clearOverlay();
        void this.startTemporalPose(video);
        this.startPostureInferenceLoop('업로드 동영상');
    }

    private stopRealtime(preserveLastOverlay: boolean = false) {
        if (this.realtimeTimer) clearTimeout(this.realtimeTimer);
        if (this.postureTimer) clearTimeout(this.postureTimer);
        this.postureLoopGeneration += 1;
        this.realtimeTimer = null;
        this.postureTimer = null;
        this.stopTemporalPose();
        this.realtime = false;
        this.videoGuideRunning = false;
        this.captureBusy = false;
        if (!preserveLastOverlay) {
            this.landmarkCount = 0;
            if (!this.previewImageSrc && !this.resultImage) this.clearOverlay();
        }
    }

    private ensureTemporalPoseWorker(generation: number): Promise<void> {
        if (this.temporalPoseWorker && this.temporalWorkerReady) return Promise.resolve();
        if (this.temporalWorkerReadyPromise) return this.temporalWorkerReadyPromise;
        this.temporalWorkerReadyPromise = new Promise<void>((resolve, reject) => {
            // Classic worker is intentional: MediaPipe's WASM loader uses importScripts.
            const worker = new Worker('/assets/temporal-pose.worker.js?v=20260819-shoulder-stability-v1');
            this.temporalPoseWorker = worker;
            let settled = false;
            const finish = (error?: Error) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(timeoutHandle);
                if (error) {
                    this.temporalPoseBusy = false;
                    worker.terminate();
                    if (this.temporalPoseWorker === worker) {
                        this.temporalPoseWorker = null;
                        this.temporalWorkerReady = false;
                    }
                    reject(error);
                } else resolve();
            };
            const timeoutHandle = window.setTimeout(() => {
                finish(new Error('브라우저 관절 추적기 초기화 시간이 초과되었습니다.'));
            }, this.temporalWorkerInitTimeoutMs);
            worker.onmessage = (event: MessageEvent) => {
                const message = event.data || {};
                if (message.type === 'initializing') {
                    this.temporalDebug.pose_backend = message.backend || 'MediaPipe Pose Landmarker · 초기화 중';
                    return;
                }
                if (message.type === 'ready') {
                    this.temporalWorkerReady = true;
                    this.temporalHardwareAcceleration = message.accelerated === true;
                    this.temporalDebug.pose_backend = message.backend;
                    finish();
                    return;
                }
                if (message.type === 'result') {
                    this.temporalPoseBusy = false;
                    if (message.generation !== this.temporalPoseGeneration) return;
                    this.consumeTemporalPoseResult(
                        { landmarks: [message.landmarks], transform: message.transform },
                        Number(message.timestamp),
                    );
                    return;
                }
                if (message.type === 'frame_error') {
                    this.temporalPoseBusy = false;
                    this.temporalDroppedFrames += 1;
                    return;
                }
                if (message.type === 'error') {
                    finish(new Error(message.message || 'MediaPipe worker initialization failed'));
                }
            };
            worker.onerror = (event: ErrorEvent) => {
                this.temporalPoseBusy = false;
                finish(new Error(event.message || 'MediaPipe worker failed'));
            };
            this.temporalHardwareAcceleration = this.hardwareWebGlAvailable();
            worker.postMessage({
                type: 'initialize',
                generation,
                prefer_gpu: this.temporalHardwareAcceleration,
            });
        });
        return this.temporalWorkerReadyPromise.finally(() => {
            this.temporalWorkerReadyPromise = null;
        });
    }

    private hardwareWebGlAvailable() {
        try {
            if (navigator.webdriver || /HeadlessChrome/i.test(navigator.userAgent)) {
                return false;
            }
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
            if (!gl) return false;
            const extension = gl.getExtension('WEBGL_debug_renderer_info');
            if (!extension) return false;
            const renderer = extension
                ? String(gl.getParameter(extension.UNMASKED_RENDERER_WEBGL) || '')
                : '';
            return Boolean(renderer) && !/swiftshader|software|llvmpipe/i.test(renderer);
        } catch (_error) {
            return false;
        }
    }

    private async startTemporalPose(video: HTMLVideoElement) {
        if (!video) return;
        this.resetTemporalRuntimeState();
        const generation = ++this.temporalPoseGeneration;
        try {
            await this.ensureTemporalPoseWorker(generation);
        } catch (error: any) {
            if (generation !== this.temporalPoseGeneration) return;
            const detail = String(error?.message || '').slice(0, 180);
            // Browser tracking is an acceleration layer. Keep the operating
            // YOLO Pose loop alive and make its fallback immediately eligible
            // instead of surfacing a fatal camera error or stopping analysis.
            this.temporalRuntimeStartedAt = performance.now() - this.temporalServerFallbackDelayMs;
            this.temporalDebug.pose_backend = detail
                ? `서버 YOLO Pose 대체 추적 · ${detail}`
                : '서버 YOLO Pose 대체 추적';
            await this.service.render();
            return;
        }
        if (generation !== this.temporalPoseGeneration || !this.realtime) return;
        this.scheduleTemporalVideoFrame(video, generation);
    }

    private scheduleTemporalVideoFrame(video: HTMLVideoElement, generation: number) {
        if (generation !== this.temporalPoseGeneration || !this.realtime) return;
        const callback = (_now: number, metadata?: any) => {
            this.temporalFrameHandle = null;
            if (generation !== this.temporalPoseGeneration || !this.realtime) return;
            if (video.paused || video.ended) return;
            this.processTemporalVideoFrame(video, metadata);
            // MediaPipe runs in a worker; this callback only submits the latest frame.
            setTimeout(() => this.scheduleTemporalVideoFrame(video, generation), 0);
        };
        const requestVideoFrame = (video as any).requestVideoFrameCallback;
        if (typeof requestVideoFrame === 'function') {
            this.temporalFrameUsesVideoCallback = true;
            this.temporalFrameHandle = requestVideoFrame.call(video, callback);
        } else {
            this.temporalFrameUsesVideoCallback = false;
            this.temporalFrameHandle = requestAnimationFrame((now) => callback(now, null));
        }
    }

    private processTemporalVideoFrame(video: HTMLVideoElement, metadata?: any) {
        if (video.paused || video.ended || video.readyState < 2
            || !this.temporalPoseWorker || !this.temporalWorkerReady) return;
        const mediaTime = Number(metadata?.mediaTime ?? video.currentTime);
        if (mediaTime === this.lastMediaTime) return;
        this.lastMediaTime = mediaTime;
        this.temporalSourceFrames += 1;
        const attemptAt = performance.now();
        const minimumPoseInterval = this.temporalHardwareAcceleration ? 33 : 80;
        if (attemptAt - this.lastPoseAttemptAt < minimumPoseInterval) return;
        this.lastPoseAttemptAt = attemptAt;
        if (this.temporalPoseBusy) {
            this.temporalDroppedFrames += 1;
            return;
        }
        this.temporalPoseBusy = true;
        const timestamp = Math.max(this.lastPoseTimestampMs + 1, Math.round(performance.now()));
        this.lastPoseTimestampMs = timestamp;
        try {
            const inputWidth = this.temporalHardwareAcceleration ? 640 : 416;
            const inputHeight = inputWidth;
            const inputScale = Math.min(
                inputWidth / video.videoWidth,
                inputHeight / video.videoHeight,
            );
            const drawWidth = Math.max(1, Math.round(video.videoWidth * inputScale));
            const drawHeight = Math.max(1, Math.round(video.videoHeight * inputScale));
            const offsetX = Math.round((inputWidth - drawWidth) / 2);
            const offsetY = Math.round((inputHeight - drawHeight) / 2);
            if (this.temporalPoseCanvas.width !== inputWidth) this.temporalPoseCanvas.width = inputWidth;
            if (this.temporalPoseCanvas.height !== inputHeight) this.temporalPoseCanvas.height = inputHeight;
            if (!this.temporalPoseContext) {
                this.temporalPoseContext = this.temporalPoseCanvas.getContext(
                    '2d',
                    { willReadFrequently: true },
                );
            }
            const poseContext = this.temporalPoseContext;
            if (!poseContext) {
                this.temporalPoseBusy = false;
                return;
            }
            poseContext.fillStyle = '#000';
            poseContext.fillRect(0, 0, inputWidth, inputHeight);
            poseContext.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
            const generation = this.temporalPoseGeneration;
            const imageData = poseContext.getImageData(0, 0, inputWidth, inputHeight);
            if (!this.temporalPoseWorker || generation !== this.temporalPoseGeneration) {
                this.temporalPoseBusy = false;
                return;
            }
            this.temporalPoseWorker.postMessage({
                type: 'frame',
                generation,
                timestamp,
                image_data: imageData,
                transform: { inputWidth, inputHeight, drawWidth, drawHeight, offsetX, offsetY },
            }, [imageData.data.buffer]);
        } catch (_error) {
            this.temporalPoseBusy = false;
        }
    }

    private consumeTemporalPoseResult(result: any, timestamp: number) {
        const source = result?.landmarks?.[0];
        if (!Array.isArray(source) || source.length !== 33) {
            if (this.lastTemporalPoseAt && timestamp - this.lastTemporalPoseAt <= this.lowConfidenceHoldMs) {
                this.queueTemporalOverlay(this.latestTemporalLandmarks);
            } else {
                this.updateTemporalGuideState(false, false);
            }
            this.updateTemporalDebug(timestamp);
            return;
        }
        const transform = result?.transform;
        const normalizedSource = transform
            ? source.map((point: any) => ({
                ...point,
                x: (Number(point.x) * transform.inputWidth - transform.offsetX) / transform.drawWidth,
                y: (Number(point.y) * transform.inputHeight - transform.offsetY) / transform.drawHeight,
            }))
            : source;
        const operationalSource = this.applyReviewedVideoPoseCorrection(normalizedSource);
        const filtered = operationalSource.map(
            (point: any, id: number) => this.filterTemporalPoint(point, id, timestamp),
        );
        if (filtered.length !== 33) return;
        this.previousTemporalLandmarks = this.latestTemporalLandmarks;
        this.latestTemporalLandmarks = filtered;
        this.temporalDetectedFrames += 1;
        this.temporalPoseFrames += 1;
        this.landmarkCount = 33;
        this.temporalMotion = this.calculateTemporalMotion(filtered, this.previousTemporalLandmarks, timestamp);
        if (this.previousTemporalLandmarks.length === 33
            && this.temporalMotion.median_displacement > 0.12) {
            this.temporalSkeletonJumpCount += 1;
        }
        this.lastTemporalPoseAt = timestamp;
        this.queueTemporalOverlay(filtered);
        if (this.corePoseReady(filtered)) {
            this.updateTemporalGuideState(true, Boolean(this.analysis?.pass));
        } else {
            this.temporalStableSince = 0;
            this.temporalGuideState = 'DETECTED';
        }
        this.updateTemporalDebug(timestamp);
    }

    private filterTemporalPoint(point: any, id: number, timestamp: number) {
        const confidence = this.clamp(Number(point?.visibility ?? point?.presence ?? 0), 0, 1);
        const previous = this.temporalPointStates.get(id);
        const keyJoint = XRAY_POSTURE_KEY_IDS.includes(id);
        const shoulderJoint = XRAY_SHOULDER_IDS.includes(id);
        const confidenceMinimum = keyJoint ? this.coreConfidenceMin : 0.12;
        const trackingConfidenceMinimum = shoulderJoint
            ? this.shoulderTrackingConfidenceMin
            : confidenceMinimum;
        if (confidence <= trackingConfidenceMinimum && previous) {
            const staleMs = Math.max(0, timestamp - previous.lastVisibleAt);
            const holdLimit = shoulderJoint
                ? this.shoulderLowConfidenceHoldMs
                : this.lowConfidenceHoldMs;
            const withinHold = staleMs <= holdLimit;
            const primaryGateJoint = XRAY_PRIMARY_GATE_IDS.includes(id);
            // Keep the last trustworthy coordinate even after the judgement
            // hold expires so the overlay cannot jump. The original low
            // confidence is retained after expiry, which blocks server scoring.
            if (withinHold || confidence <= confidenceMinimum || !primaryGateJoint) {
                const heldConfidence = (withinHold || !primaryGateJoint)
                    ? Math.max(confidenceMinimum + 0.001, previous.confidence * 0.92)
                    : confidence;
                return {
                    id,
                    x: previous.x.filtered,
                    y: previous.y.filtered,
                    visibility: heldConfidence,
                    confidence: heldConfidence,
                    source_confidence: confidence,
                    held: true,
                    stale_ms: staleMs,
                };
            }
        }
        const rawX = this.clamp(Number(point?.x ?? 0), 0, 1);
        const rawY = this.clamp(Number(point?.y ?? 0), 0, 1);
        const x = this.filterOneEuroAxis(previous?.x, rawX, timestamp, id);
        const y = this.filterOneEuroAxis(previous?.y, rawY, timestamp, id);
        this.temporalPointStates.set(id, {
            x,
            y,
            lastVisibleAt: confidence > trackingConfidenceMinimum
                ? timestamp
                : (previous?.lastVisibleAt || 0),
            confidence,
        });
        return {
            id,
            x: x.filtered,
            y: y.filtered,
            visibility: confidence,
            confidence,
            source_confidence: confidence,
            micro_motion: Boolean(x.microMotion || y.microMotion),
            held: false,
            stale_ms: 0,
        };
    }

    private async loadReviewedVideoPoseCorrections(file: File, generation: number) {
        try {
            if (!window.crypto?.subtle) return;
            const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
            if (generation !== this.poseCorrectionLoadGeneration || this.uploadedVideoFile !== file) return;
            const sourceVideoSha256 = Array.from(new Uint8Array(digest))
                .map(value => value.toString(16).padStart(2, '0'))
                .join('');
            const form = new FormData();
            form.append('source_video_sha256', sourceVideoSha256);
            const response = await fetch(
                '/wiz/api/page.xray.normality_review_runtime_v13/pose_corrections',
                { method: 'POST', body: form, credentials: 'same-origin' },
            );
            const payload = await response.json();
            if (generation !== this.poseCorrectionLoadGeneration || this.uploadedVideoFile !== file) return;
            this.uploadedVideoSha256 = sourceVideoSha256;
            this.reviewedVideoPoseCorrections = response.ok && payload?.code === 200
                ? (payload?.data?.result?.corrections || [])
                : [];
            this.temporalDebug.pose_review_correction_count = this.reviewedVideoPoseCorrections.length;
            await this.service.render();
        } catch (_error) {
            if (generation !== this.poseCorrectionLoadGeneration) return;
            this.reviewedVideoPoseCorrections = [];
            this.temporalDebug.pose_review_correction_count = 0;
        }
    }

    private applyReviewedVideoPoseCorrection(points: any[]) {
        this.temporalDebug.pose_review_correction_applied = false;
        if (!this.videoUploadMode || !this.uploadedVideoSha256
            || !this.reviewedVideoPoseCorrections.length) return points;
        const video = document.getElementById('xray-video') as HTMLVideoElement;
        const sourceTime = Number(video?.currentTime || 0);
        let nearest: any = null;
        let nearestDistance = Number.POSITIVE_INFINITY;
        for (const correction of this.reviewedVideoPoseCorrections) {
            const distance = Math.abs(sourceTime - Number(correction?.video_time_seconds || 0));
            if (distance < nearestDistance) {
                nearest = correction;
                nearestDistance = distance;
            }
        }
        // Older reviews did not persist the model coordinates that existed before
        // the human edit.  Capture that reference while playback crosses the saved
        // frame so the same human-model delta can track subsequent frames too.
        if (nearest && nearestDistance <= .15
            && (!Array.isArray(nearest.pose_reference_keypoints)
                || nearest.pose_reference_keypoints.length !== XRAY_POSTURE_KEY_IDS.length)) {
            nearest.pose_reference_keypoints = points
                .map((point: any, index: number) => ({
                    ...point,
                    id: Number(point?.id ?? index),
                }))
                .filter((point: any) => XRAY_POSTURE_KEY_IDS.includes(Number(point.id)));
        }
        const trackingAnchors = this.reviewedVideoPoseCorrections.filter((correction: any) => (
            Array.isArray(correction?.pose_reference_keypoints)
            && correction.pose_reference_keypoints.length === XRAY_POSTURE_KEY_IDS.length
        ));
        if (trackingAnchors.length) {
            let left = trackingAnchors[0];
            let right = trackingAnchors[trackingAnchors.length - 1];
            for (const anchor of trackingAnchors) {
                const anchorTime = Number(anchor.video_time_seconds || 0);
                if (anchorTime <= sourceTime) left = anchor;
                if (anchorTime >= sourceTime) { right = anchor; break; }
            }
            const leftTime = Number(left.video_time_seconds || 0);
            const rightTime = Number(right.video_time_seconds || 0);
            const mix = rightTime > leftTime
                ? this.clamp((sourceTime - leftTime) / (rightTime - leftTime), 0, 1)
                : 0;
            const deltaMap = (anchor: any) => {
                const saved = new Map<number, any>(
                    (anchor.pose_keypoints || []).map((point: any) => [Number(point.id), point]),
                );
                const reference = new Map<number, any>(
                    (anchor.pose_reference_keypoints || []).map((point: any) => [Number(point.id), point]),
                );
                return { saved, reference };
            };
            const leftMap = deltaMap(left);
            const rightMap = deltaMap(right);
            const nearestSaved = new Map<number, any>(
                (nearest?.pose_keypoints || []).map((point: any) => [Number(point.id), point]),
            );
            const anchorWeight = this.clamp(1 - (nearestDistance / .75), 0, 1);
            this.temporalDebug.pose_review_correction_applied = true;
            this.temporalDebug.pose_review_correction_time = Number(nearest?.video_time_seconds || 0);
            return points.map((point: any, index: number) => {
                const id = Number(point?.id ?? index);
                if (!XRAY_POSTURE_KEY_IDS.includes(id)) return point;
                const leftSaved = leftMap.saved.get(id);
                const leftReference = leftMap.reference.get(id);
                const rightSaved = rightMap.saved.get(id);
                const rightReference = rightMap.reference.get(id);
                if (!leftSaved || !leftReference || !rightSaved || !rightReference) return point;
                const dx = (Number(leftSaved.x) - Number(leftReference.x)) * (1 - mix)
                    + (Number(rightSaved.x) - Number(rightReference.x)) * mix;
                const dy = (Number(leftSaved.y) - Number(leftReference.y)) * (1 - mix)
                    + (Number(rightSaved.y) - Number(rightReference.y)) * mix;
                const trackedX = this.clamp(Number(point.x) + dx, 0, 1);
                const trackedY = this.clamp(Number(point.y) + dy, 0, 1);
                const anchorSaved = nearestSaved.get(id);
                return {
                    ...point,
                    x: anchorSaved
                        ? this.clamp(trackedX * (1 - anchorWeight) + Number(anchorSaved.x) * anchorWeight, 0, 1)
                        : trackedX,
                    y: anchorSaved
                        ? this.clamp(trackedY * (1 - anchorWeight) + Number(anchorSaved.y) * anchorWeight, 0, 1)
                        : trackedY,
                    review_correction_weight: 1,
                    review_correction_mode: 'tracking_offset',
                };
            });
        }
        const radiusSeconds = .5;
        if (!nearest || nearestDistance > radiusSeconds) return points;
        const weight = this.clamp(1 - (nearestDistance / radiusSeconds), 0, 1);
        const savedById = new Map<number, any>(
            (nearest.pose_keypoints || []).map((point: any) => [Number(point.id), point]),
        );
        this.temporalDebug.pose_review_correction_applied = weight > .01;
        this.temporalDebug.pose_review_correction_time = Number(nearest.video_time_seconds || 0);
        return points.map((point: any, id: number) => {
            const saved = savedById.get(id);
            if (!saved || !XRAY_POSTURE_KEY_IDS.includes(id)) return point;
            return {
                ...point,
                x: this.clamp((Number(point.x) * (1 - weight)) + (Number(saved.x) * weight), 0, 1),
                y: this.clamp((Number(point.y) * (1 - weight)) + (Number(saved.y) * weight), 0, 1),
                review_correction_weight: weight,
            };
        });
    }

    private corePoseReady(landmarks: any[]) {
        return this.poseIdsReady(landmarks, XRAY_PRIMARY_GATE_IDS);
    }

    private realtimeOverlayLandmarks(data: XrayAnalysisResponse) {
        return this.corePoseReady(this.latestTemporalLandmarks)
            ? this.latestTemporalLandmarks
            : (Array.isArray(data?.landmarks) ? data.landmarks : []);
    }

    private poseIdsReady(
        landmarks: any[],
        pointIds: number[],
        requireSourceConfidence: boolean = false,
    ) {
        if (!Array.isArray(landmarks) || landmarks.length !== 33) return false;
        return pointIds.every((id) => {
            const point = landmarks[id];
            const shoulderJoint = XRAY_SHOULDER_IDS.includes(id);
            const holdLimit = shoulderJoint
                ? this.shoulderLowConfidenceHoldMs
                : this.lowConfidenceHoldMs;
            const held = Boolean(point?.held);
            const confidence = Number(
                requireSourceConfidence && !held
                    ? (point?.source_confidence ?? point?.confidence ?? point?.visibility ?? 0)
                    : (point?.confidence ?? point?.visibility ?? 0),
            );
            const heldTooLong = held
                && Number(point?.stale_ms || 0) > holdLimit;
            return confidence > this.coreConfidenceMin && !heldTooLong;
        });
    }

    private filterOneEuroAxis(
        previous: FilterAxisState | undefined,
        raw: number,
        timestamp: number,
        pointId: number,
    ): FilterAxisState {
        if (!previous) {
            return {
                raw, filtered: raw, derivative: 0, timestamp,
                microDirection: 0, microFrames: 0, microMotion: false,
            };
        }
        const dt = Math.max(0.001, (timestamp - previous.timestamp) / 1000);
        const deltaFromFiltered = raw - previous.filtered;
        const rawDirection = Math.sign(raw - previous.raw);
        const microFrames = rawDirection === 0
            ? 0
            : rawDirection === previous.microDirection
                ? Number(previous.microFrames || 0) + 1
                : 1;
        const persistentMicroMotion = microFrames >= this.temporalMicroMotionReleaseFrames;
        const shoulderJoint = XRAY_SHOULDER_IDS.includes(pointId);
        const staticDeadband = shoulderJoint
            ? this.shoulderStaticDeadband
            : this.temporalStaticDeadband;
        if (Math.abs(deltaFromFiltered) <= staticDeadband && !persistentMicroMotion) {
            return {
                raw,
                filtered: previous.filtered,
                derivative: previous.derivative * 0.5,
                timestamp,
                microDirection: rawDirection,
                microFrames,
                microMotion: false,
            };
        }
        const derivative = (raw - previous.raw) / dt;
        const derivativeAlpha = this.oneEuroAlpha(dt, 1.0);
        const filteredDerivative = derivativeAlpha * derivative + (1 - derivativeAlpha) * previous.derivative;
        // Static noise keeps the strong cutoff, while monotonic micro-motion is
        // released after three frames so deliberate slow movement is not delayed.
        const baseCutoff = shoulderJoint ? 0.42 : 0.55;
        const beta = shoulderJoint ? 0.04 : 0.08;
        const movingCutoff = shoulderJoint ? 2.2 : 3.0;
        const adaptiveCutoff = baseCutoff + beta * Math.abs(filteredDerivative);
        const cutoff = persistentMicroMotion
            ? Math.max(movingCutoff, adaptiveCutoff)
            : adaptiveCutoff;
        const alpha = this.oneEuroAlpha(dt, cutoff);
        return {
            raw,
            filtered: alpha * raw + (1 - alpha) * previous.filtered,
            derivative: filteredDerivative,
            timestamp,
            microDirection: rawDirection,
            microFrames,
            microMotion: persistentMicroMotion,
        };
    }

    private oneEuroAlpha(dt: number, cutoff: number) {
        const tau = 1 / (2 * Math.PI * cutoff);
        return 1 / (1 + tau / dt);
    }

    private queueTemporalOverlay(landmarks: any[]) {
        if (!landmarks.length) return;
        if (this.normalSkeletonEnabled
            && !this.personalizedSkeleton.length
            && this.hasCalibrationJoints(landmarks)) {
            this.personalizedSkeleton = this.buildPersonalizedTargetSkeleton(landmarks);
            this.adaptiveMessage = '첫 관절 프레임의 체형 크기로 정상 자세 형상을 생성해 고정했습니다.';
        }
        this.lastTemporalRenderAt = performance.now();
        this.temporalRenderFrames += 1;
        this.queueSmoothOverlay(landmarks, Boolean(this.analysis?.pass));
    }

    private startPostureInferenceLoop(sourceLabel: string) {
        if (this.postureTimer) clearTimeout(this.postureTimer);
        const generation = ++this.postureLoopGeneration;
        this.lastPostureRequestAt = 0;
        const tick = () => {
            if (!this.realtime || generation !== this.postureLoopGeneration) return;
            const video = document.getElementById('xray-video') as HTMLVideoElement;
            const now = performance.now();
            const effectiveInterval = this.postureInferenceIntervalMs;
            const hasCachedBucky = Boolean(this.cachedBuckyDetectorConfig());
            const buckyRequestAllowed = hasCachedBucky
                || this.detectorLockCandidates.length > 0
                // The server Detector requires four temporal acquisition frames.
                // Guarantee that initial burst even when one inference response
                // takes longer than the five-second browser validation window.
                || this.detectorSearchFailureCount < this.detectorInitialAcquisitionAttempts
                || now < this.detectorValidationUntil
                || !this.lastBuckyDetectionAttemptAt
                || now - this.lastBuckyDetectionAttemptAt >= this.buckyFailureRetryIntervalMs;
            const sourceActive = Boolean(video && !video.paused && !video.ended && video.readyState >= 2);
            const browserPoseReady = this.corePoseReady(this.latestTemporalLandmarks);
            const serverPoseFallbackDue = Boolean(
                this.temporalRuntimeStartedAt
                && now - this.temporalRuntimeStartedAt >= this.temporalServerFallbackDelayMs
                && !browserPoseReady
            );
            if (sourceActive
                && (browserPoseReady || serverPoseFallbackDue)
                && !this.captureBusy
                && buckyRequestAllowed
                && now >= this.postureRetryAfter
                && now - this.lastPostureRequestAt >= effectiveInterval) {
                this.lastPostureRequestAt = now;
                void this.captureOnce(true, sourceLabel);
            }
            if (this.realtime && generation === this.postureLoopGeneration) {
                this.postureTimer = setTimeout(tick, 50);
            }
        };
        tick();
    }

    public async recalibrateDetector() {
        if (!this.realtime) return;
        this.resetNormalSkeletonForDetectorRecalibration();
        this.resetRealtimeDetectorForNewSource(
            '촬영판 자동 재보정을 시작합니다. 잠시 움직이지 말아 주세요.',
        );
        await this.service.render();
    }

    private resetNormalSkeletonForDetectorRecalibration() {
        this.calibrationSamples = [];
        this.calibrationState = 'idle';
        this.calibrationProgress = 0;
        this.personalizedSkeleton = [];

        if (!this.normalSkeletonEnabled) {
            this.adaptiveMessage = '';
            return;
        }

        const currentLandmarks = this.latestTemporalLandmarks.length
            ? this.latestTemporalLandmarks
            : (this.analysis?.landmarks || []);
        if (this.hasCalibrationJoints(currentLandmarks)) {
            this.prepareNormalReferenceSkeleton(currentLandmarks);
            this.adaptiveMessage = '디텍터 재보정 시점의 환자 위치와 체형을 기준으로 정상 자세 가이드를 다시 설정했습니다.';
            return;
        }

        // The temporal pose loop keeps running during Detector reacquisition.
        // queueTemporalOverlay() will build the new guide from the first valid
        // post-click pose instead of restoring the stale pre-click skeleton.
        this.adaptiveMessage = '디텍터 재보정 후 관절이 확인되는 첫 프레임에서 정상 자세 가이드를 다시 설정합니다.';
    }

    private resetRealtimeDetectorForNewSource(message: string) {
        // Stored/manual coordinates belong to the image or camera source that
        // produced them. A new source must execute the operating Detector
        // again instead of silently reusing stale corners.
        this.detectorCalibrationRevision += 1;
        this.trackingSessionId = this.createTrackingSessionId();
        this.detectorCalibrationMode = false;
        this.detectorCalibrated = false;
        this.detectorCorners = [];
        this.buckyDetection = null;
        this.lastBuckyDetectionAt = 0;
        this.lastBuckyDetectionAttemptAt = 0;
        this.detectorSearchFailureCount = 0;
        this.lastDetectorFailureData = null;
        this.detectorValidationUntil = performance.now() + this.detectorValidationWindowMs;
        this.resetDetectorLockCandidates(message);
        this.clearDetectorOverlayState();
    }

    private acceptDetectorLockCandidate(detection: any) {
        const candidate = this.normalizeDetectorLockCandidate(detection);
        if (!candidate) {
            this.detectorSearchFailureCount += 1;
            const cutoff = performance.now() - this.detectorCandidateMemoryMs;
            this.detectorLockCandidates = this.detectorLockCandidates.filter(
                (item) => item.observedAt >= cutoff,
            );
            this.detectorLockSampleCount = this.detectorLockCandidates.length;
            this.detectorLockState = this.detectorLockSampleCount ? 'verifying' : 'searching';
            this.detectorMessage = this.detectorRecoveryMessage(
                detection?.review_reason || detection?.reason,
                true,
            );
            return false;
        }

        // The persistent inference worker already performs confidence,
        // geometry, edge/prior and multi-frame continuity validation. Do not
        // require another three consecutive browser responses after that
        // server-side lock; occlusion can hide the panel again immediately.
        if (this.serverValidatedDetector(detection)) {
            this.applyBuckyDetection(detection);
            this.detectorValidationUntil = 0;
            this.detectorLockState = 'locked';
            this.detectorLockSampleCount = this.detectorLockRequiredFrames;
            this.detectorSearchFailureCount = 0;
            this.lastDetectorFailureData = null;
            this.detectorMessage = '촬영판 자동 인식 완료 · 서버 다중 프레임 검증을 통과했습니다.';
            return true;
        }

        const cutoff = candidate.observedAt - this.detectorCandidateMemoryMs;
        this.detectorLockCandidates = this.detectorLockCandidates.filter((item) => (
            item.observedAt >= cutoff && this.detectorCandidatesSimilar(item, candidate)
        ));
        this.detectorLockCandidates.push(candidate);
        this.detectorLockCandidates = this.detectorLockCandidates.slice(
            -this.detectorLockRequiredFrames,
        );
        this.detectorLockSampleCount = this.detectorLockCandidates.length;
        this.detectorLockState = this.detectorLockSampleCount >= this.detectorLockRequiredFrames
            ? 'locked'
            : 'verifying';
        if (this.detectorLockState !== 'locked') {
            this.detectorMessage = `촬영판 위치 확인 중 ${this.detectorLockSampleCount}/${this.detectorLockRequiredFrames} · 중간에 가려져도 같은 위치의 후보를 누적합니다.`;
            return false;
        }

        const averaged = this.averageDetectorLockCandidate(this.detectorLockCandidates);
        this.applyBuckyDetection(averaged);
        this.detectorValidationUntil = 0;
        this.detectorLockState = 'locked';
        this.detectorLockSampleCount = this.detectorLockRequiredFrames;
        this.detectorSearchFailureCount = 0;
        this.lastDetectorFailureData = null;
        this.detectorMessage = `촬영판 실시간 추적 시작 · ${this.detectorLockRequiredFrames}개 일치 후보 검증 완료`;
        return true;
    }

    private serverValidatedDetector(detection: any) {
        const config = detection?.detector_config || {};
        const source = String(
            detection?.BUCKY_SOURCE
            || detection?.source
            || config?.BUCKY_SOURCE
            || config?.source
            || '',
        ).toUpperCase();
        return source.startsWith('YOLO_OBB_TEMPORAL_')
            || source === 'TEMPORAL_DETECTOR_LOCK_AVERAGE';
    }

    private detectorRecoveryMessage(reason: any, searching: boolean = false) {
        const normalizedReason = String(reason || '').toUpperCase();
        const prefix = searching
            ? `촬영판 자동 탐색 중 · ${this.detectorSearchFailureCount}회 재시도했습니다. `
            : '촬영판 위치를 자동으로 확정하지 못했습니다. ';
        if (normalizedReason.includes('LOW_CONFIDENCE')
            || normalizedReason.includes('PARTIAL')
            || normalizedReason.includes('EDGE')
            || normalizedReason.includes('NOT_DETECTED')) {
            return `${prefix}몸에 가려진 촬영판 테두리가 더 보이도록 한 걸음 떨어져 1~2초 서 있다가, 파란 촬영판 표시가 나타난 뒤 천천히 촬영 자세를 잡아 주세요.`;
        }
        return `${prefix}다시 촬영할 때 처음 1~2초는 촬영판의 위·아래·좌·우 테두리가 보이게 한 걸음 떨어져 서 주세요.`;
    }

    private acceptTrackedDetectorUpdate(detection: any) {
        const candidate = this.normalizeDetectorLockCandidate(detection);
        if (!candidate) return false;
        this.applyBuckyDetection(detection);
        this.detectorLockState = 'locked';
        this.detectorLockSampleCount = this.detectorLockRequiredFrames;
        this.detectorMessage = '촬영판 실시간 추적 중 · YOLO OBB 좌표를 매 프레임 갱신합니다.';
        return true;
    }

    private normalizeDetectorLockCandidate(detection: any): DetectorCandidate | null {
        if (!detection?.detected) return null;
        const config = detection?.detector_config || {};
        const sourceCorners = detection?.corners || config?.corners || detection?.bucky_bbox || {};
        const names = ['top_left', 'top_right', 'bottom_right', 'bottom_left'];
        const corners = names.map((name) => sourceCorners?.[name]);
        if (corners.some((point) => !point
            || !Number.isFinite(Number(point.x))
            || !Number.isFinite(Number(point.y)))) return null;
        const normalized = corners.map((point) => ({
            x: Number(point.x),
            y: Number(point.y),
        }));
        if (normalized.some((point) => point.x < 0 || point.x > 1 || point.y < 0 || point.y > 1)) {
            return null;
        }
        const area = Math.abs(normalized.reduce((sum, point, index) => {
            const next = normalized[(index + 1) % normalized.length];
            return sum + point.x * next.y - next.x * point.y;
        }, 0) / 2);
        const edge = (first: any, second: any) => Math.hypot(
            second.x - first.x,
            second.y - first.y,
        );
        const width = (edge(normalized[0], normalized[1]) + edge(normalized[3], normalized[2])) / 2;
        const height = (edge(normalized[0], normalized[3]) + edge(normalized[1], normalized[2])) / 2;
        const ratio = width / Math.max(height, 1e-6);
        const center = detection?.center || config?.center_cross || {
            x: normalized.reduce((sum, point) => sum + point.x, 0) / 4,
            y: normalized.reduce((sum, point) => sum + point.y, 0) / 4,
        };
        const confidence = Number(detection?.confidence ?? config?.confidence ?? 1);
        if (area < 0.06 || area > 0.95 || width < 0.18 || height < 0.18
            || ratio < 0.35 || ratio > 1.8
            || Number(center.x) < 0.03 || Number(center.x) > 0.97
            || Number(center.y) < 0.03 || Number(center.y) > 0.97
            || !Number.isFinite(confidence) || confidence < 0.25) return null;
        return {
            detection: { ...detection, confidence },
            corners: normalized,
            center: { ...center, x: Number(center.x), y: Number(center.y) },
            area,
            observedAt: performance.now(),
        };
    }

    private detectorCandidatesSimilar(first: DetectorCandidate, second: DetectorCandidate) {
        const cornerRms = Math.sqrt(first.corners.reduce((sum, point, index) => {
            const other = second.corners[index];
            return sum + Math.pow(point.x - other.x, 2) + Math.pow(point.y - other.y, 2);
        }, 0) / first.corners.length);
        const centerDrift = Math.hypot(
            Number(first.center.x) - Number(second.center.x),
            Number(first.center.y) - Number(second.center.y),
        );
        const areaChange = Math.abs(first.area - second.area) / Math.max(first.area, 1e-6);
        return cornerRms <= this.detectorCandidateMaxCornerDrift
            && centerDrift <= 0.04
            && areaChange <= 0.18;
    }

    private averageDetectorLockCandidate(candidates: DetectorCandidate[]) {
        const names = ['top_left', 'top_right', 'bottom_right', 'bottom_left'];
        const corners: any = {};
        names.forEach((name, index) => {
            corners[name] = {
                x: candidates.reduce((sum, item) => sum + item.corners[index].x, 0) / candidates.length,
                y: candidates.reduce((sum, item) => sum + item.corners[index].y, 0) / candidates.length,
            };
        });
        const center = {
            x: candidates.reduce((sum, item) => sum + Number(item.center.x), 0) / candidates.length,
            y: candidates.reduce((sum, item) => sum + Number(item.center.y), 0) / candidates.length,
            source: 'temporal_detector_lock_average',
        };
        const confidence = candidates.reduce(
            (sum, item) => sum + Number(item.detection?.confidence || 0),
            0,
        ) / candidates.length;
        return {
            ...candidates[candidates.length - 1].detection,
            detected: true,
            confidence,
            corners,
            center,
            detector_config: {
                version: 2,
                source: 'temporal_detector_lock_average',
                confidence,
                corners,
                center_cross: center,
            },
        };
    }

    private resetDetectorLockCandidates(message?: string) {
        this.detectorLockCandidates = [];
        this.detectorLockSampleCount = 0;
        this.detectorLockState = 'searching';
        if (message) this.detectorMessage = message;
    }

    private clearDetectorOverlayState() {
        this.overlayStartDetectorCorners = [];
        this.overlayTargetDetectorCorners = [];
        this.overlayRenderedDetectorCorners = [];
        this.overlayStartDetectorCenter = null;
        this.overlayTargetDetectorCenter = null;
        this.overlayRenderedDetectorCenter = null;
        requestAnimationFrame(() => this.drawCurrentOverlayFrame());
    }

    private cachedBuckyDetectorConfig() {
        if (!this.detectorCalibrated || this.detectorCorners.length !== 4) return null;
        const [topLeft, topRight, bottomRight, bottomLeft] = this.detectorCorners;
        return {
            version: 2,
            source: 'temporal_cached_yolo_bucky',
            confidence: Number(this.buckyDetection?.confidence || 0),
            corners: {
                top_left: topLeft,
                top_right: topRight,
                bottom_right: bottomRight,
                bottom_left: bottomLeft,
            },
            center_cross: this.buckyDetection?.center || undefined,
        };
    }

    private calculateTemporalMotion(current: any[], previous: any[], timestamp: number) {
        const dt = previous.length && this.lastTemporalPoseAt
            ? Math.max(0.001, (timestamp - this.lastTemporalPoseAt) / 1000)
            : 0;
        const joints = current.map((point, id) => {
            const before = previous?.[id];
            const dx = before ? point.x - before.x : 0;
            const dy = before ? point.y - before.y : 0;
            const displacement = Math.hypot(dx, dy);
            return { id, x: point.x, y: point.y, confidence: point.confidence, dx, dy, displacement, velocity: dt ? displacement / dt : 0 };
        });
        const displacementValues = joints.map((joint) => joint.displacement);
        const sortedDisplacements = [...displacementValues].sort((a, b) => a - b);
        const buckyCenter = this.buckyDetection?.center || null;
        const relativeToBucky = buckyCenter
            ? joints.map((joint) => ({ id: joint.id, x: joint.x - buckyCenter.x, y: joint.y - buckyCenter.y }))
            : [];
        return {
            timestamp,
            joints,
            mean_displacement: displacementValues.reduce((sum, value) => sum + value, 0) / Math.max(1, displacementValues.length),
            median_displacement: sortedDisplacements[Math.floor(sortedDisplacements.length / 2)] || 0,
            max_displacement: Math.max(0, ...displacementValues),
            relative_to_bucky: relativeToBucky,
            shoulder_angle_deg: this.segmentAngle(current[11], current[12]),
            hip_angle_deg: this.segmentAngle(current[23], current[24]),
            torso_angle_deg: this.segmentAngle(
                this.midpoint(current[11], current[12]),
                this.midpoint(current[23], current[24]),
            ),
            shoulder_to_bucky_distance: buckyCenter
                ? this.distance(this.midpoint(current[11], current[12]), buckyCenter)
                : null,
        };
    }

    private segmentAngle(first: any, second: any) {
        if (!first || !second) return null;
        return Math.atan2(second.y - first.y, second.x - first.x) * 180 / Math.PI;
    }

    private updateTemporalGuideState(detected: boolean, passed: boolean) {
        const now = performance.now();
        if (!detected) {
            this.temporalStableSince = 0;
            this.temporalGuideState = 'SEARCHING';
            return;
        }
        if (this.temporalDetectedFrames < 3 || !this.analysis) {
            this.temporalGuideState = 'DETECTED';
            return;
        }
        if (!passed) {
            this.temporalStableSince = 0;
            this.temporalGuideState = 'CORRECTING';
            return;
        }
        if (!this.temporalStableSince) this.temporalStableSince = now;
        this.temporalGuideState = now - this.temporalStableSince >= this.temporalStableDurationMs
            ? 'PASS'
            : 'STABLE';
    }

    private recordTemporalPostureResult(passed: boolean) {
        const now = performance.now();
        if (this.temporalLastPosturePass !== null && this.temporalLastPosturePass !== passed) {
            if (this.temporalLastPostureChangeAt
                && now - this.temporalLastPostureChangeAt < this.temporalStableDurationMs) {
                this.temporalPostureFlickerCount += 1;
            }
            this.temporalLastPostureChangeAt = now;
        } else if (this.temporalLastPosturePass === null) {
            this.temporalLastPostureChangeAt = now;
        }
        this.temporalLastPosturePass = passed;
    }

    private updateTemporalDebug(now: number) {
        if (!this.temporalMetricsWindowStartedAt) this.temporalMetricsWindowStartedAt = now;
        const elapsed = now - this.temporalMetricsWindowStartedAt;
        const confidence = this.latestTemporalLandmarks.length
            ? this.latestTemporalLandmarks.reduce((sum, point) => sum + Number(point.confidence || 0), 0)
                / this.latestTemporalLandmarks.length
            : 0;
        const corePoints = XRAY_PRIMARY_GATE_IDS
            .map((id) => this.latestTemporalLandmarks[id])
            .filter(Boolean);
        const coreConfidence = corePoints.length === XRAY_PRIMARY_GATE_IDS.length
            ? corePoints.reduce(
                (sum, point) => sum + Number(point.source_confidence ?? point.confidence ?? 0),
                0,
            ) / corePoints.length
            : 0;
        this.temporalDebug.overlay_age_ms = this.lastTemporalRenderAt
            ? Math.round(performance.now() - this.lastTemporalRenderAt)
            : 0;
        this.temporalDebug.tracking_confidence = confidence;
        this.temporalDebug.core_tracking_confidence = coreConfidence;
        this.temporalDebug.core_pose_ready = this.corePoseReady(this.latestTemporalLandmarks);
        this.temporalDebug.held_core_joints = corePoints.filter((point) => point?.held).length;
        this.temporalDebug.dropped_frames = this.temporalDroppedFrames;
        this.temporalDebug.skeleton_jump_count = this.temporalSkeletonJumpCount;
        this.temporalDebug.posture_flicker_count = this.temporalPostureFlickerCount;
        this.temporalDebug.bucky_age_ms = this.lastBuckyDetectionAt
            ? Math.round(performance.now() - this.lastBuckyDetectionAt)
            : null;
        if (elapsed < 1000) return;
        const seconds = elapsed / 1000;
        this.temporalDebug.source_fps = this.temporalSourceFrames / seconds;
        this.temporalDebug.pose_fps = this.temporalPoseFrames / seconds;
        this.temporalDebug.skeleton_render_fps = this.temporalRenderFrames / seconds;
        this.temporalDebug.posture_inference_fps = this.temporalPostureResponses / seconds;
        this.temporalMetricsWindowStartedAt = now;
        this.temporalSourceFrames = 0;
        this.temporalPoseFrames = 0;
        this.temporalRenderFrames = 0;
        this.temporalPostureResponses = 0;
        try {
            this.ref.detectChanges();
        } catch (_error) { }
    }

    private resetTemporalRuntimeState() {
        this.stopTemporalPose();
        this.latestTemporalLandmarks = [];
        this.previousTemporalLandmarks = [];
        this.temporalPointStates.clear();
        this.temporalRuntimeStartedAt = performance.now();
        this.lastTemporalPoseAt = 0;
        this.lastTemporalRenderAt = 0;
        this.lastMediaTime = -1;
        this.lastPoseTimestampMs = 0;
        this.lastPoseAttemptAt = 0;
        this.temporalDetectedFrames = 0;
        this.temporalStableSince = 0;
        this.lastBuckyDetectionAt = 0;
        this.lastBuckyDetectionAttemptAt = 0;
        // resetRealtimeDetectorForNewSource() opens a short burst window so the
        // temporal Detector can collect consecutive acquisition frames. Do not
        // erase that window while the browser pose runtime starts.
        if (!this.detectorCalibrated) {
            this.detectorValidationUntil = Math.max(
                this.detectorValidationUntil,
                performance.now() + this.detectorValidationWindowMs,
            );
        }
        this.temporalMetricsWindowStartedAt = performance.now();
        this.temporalSourceFrames = 0;
        this.temporalPoseFrames = 0;
        this.temporalRenderFrames = 0;
        this.temporalPostureResponses = 0;
        this.temporalDroppedFrames = 0;
        this.temporalSkeletonJumpCount = 0;
        this.temporalPostureFlickerCount = 0;
        this.temporalLastPosturePass = null;
        this.temporalLastPostureChangeAt = 0;
        this.postureFailureCount = 0;
        this.postureRetryAfter = 0;
        this.temporalGuideState = 'SEARCHING';
        this.temporalMotion = null;
    }

    private stopTemporalPose() {
        this.temporalPoseGeneration += 1;
        if (this.temporalFrameHandle !== null) {
            const video = document.getElementById('xray-video') as HTMLVideoElement;
            if (this.temporalFrameUsesVideoCallback && typeof (video as any)?.cancelVideoFrameCallback === 'function') {
                (video as any).cancelVideoFrameCallback(this.temporalFrameHandle);
            } else {
                cancelAnimationFrame(this.temporalFrameHandle);
            }
        }
        this.temporalFrameHandle = null;
        this.temporalPoseBusy = false;
        this.temporalPoseWorker?.terminate();
        this.temporalPoseWorker = null;
        this.temporalWorkerReady = false;
        this.temporalWorkerReadyPromise = null;
    }

    private resetUploadedVideoElement() {
        const video = document.getElementById('xray-video') as HTMLVideoElement;
        if (!video) return;
        video.onloadeddata = null;
        video.onplay = null;
        video.onpause = null;
        video.onended = null;
        if (!this.stream) video.pause();
        if (!this.stream) video.removeAttribute('src');
        if (!this.stream) video.load();
        this.uploadedVideoFile = null;
        this.poseCorrectionLoadGeneration += 1;
        this.uploadedVideoSha256 = '';
        this.reviewedVideoPoseCorrections = [];
        this.videoUploadMode = false;
        this.videoGuideRunning = false;
    }

    private queueSmoothOverlay(landmarks: any[], passed: boolean) {
        const nextLandmarks = this.cloneOverlayPoints(landmarks);
        const nextDetectorCorners = this.cloneOverlayPoints(this.detectorCorners);
        const nextDetectorCenter = this.cloneOverlayPoint(this.buckyDetection?.center);
        const now = performance.now();
        const hasRenderedOverlay = Boolean(
            this.overlayRenderedLandmarks.length
            || this.overlayRenderedDetectorCorners.length
        );
        const discontinuous = this.overlayMotionIsDiscontinuous(
            this.overlayRenderedLandmarks,
            nextLandmarks,
            this.overlayRenderedDetectorCorners,
            nextDetectorCorners,
        );
        if (!discontinuous
            && this.overlayPoseChangeIsNegligible(this.overlayTargetLandmarks, nextLandmarks)) {
            this.overlayPassed = passed;
            return;
        }

        this.overlayStartLandmarks = this.cloneOverlayPoints(
            this.overlayRenderedLandmarks
        );
        this.overlayStartDetectorCorners = this.cloneOverlayPoints(
            this.overlayRenderedDetectorCorners
        );
        this.overlayStartDetectorCenter = this.cloneOverlayPoint(
            this.overlayRenderedDetectorCenter
        );
        this.overlayTargetLandmarks = nextLandmarks;
        this.overlayTargetDetectorCorners = nextDetectorCorners;
        this.overlayTargetDetectorCenter = nextDetectorCenter;
        this.overlayPassed = passed;

        const targetInterval = this.overlayLastTargetAt
            ? now - this.overlayLastTargetAt
            : 120;
        this.overlayLastTargetAt = now;
        this.overlayTransitionStartedAt = now;
        this.overlayTransitionDurationMs = (!hasRenderedOverlay || discontinuous)
            ? 0
            : this.clamp(targetInterval * 0.8, 80, 240);

        if (!hasRenderedOverlay || discontinuous) {
            this.overlayRenderedLandmarks = this.cloneOverlayPoints(nextLandmarks);
            this.overlayRenderedDetectorCorners = this.cloneOverlayPoints(
                nextDetectorCorners
            );
            this.overlayRenderedDetectorCenter = this.cloneOverlayPoint(
                nextDetectorCenter
            );
            this.drawCurrentOverlayFrame();
        }
        this.startOverlayAnimation();
    }

    private createTrackingSessionId() {
        return `pose-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    }

    private compactRealtimeClientData(data: XrayAnalysisResponse) {
        // Older running API workers can temporarily return the full diagnostic
        // object. Do not retain/render duplicate model traces on every frame.
        for (const field of [
            'pose',
            'pipeline',
            'score_basis',
            'inference_trace',
            'reference_validation',
            'detector_body_relation',
            'posture_model_comparison',
            'detector_result',
            'legacy_result',
            'trained_result',
            'posture_result',
            'bucky',
            'bucky_model',
            'detector_config',
            'keypoints',
            'skeleton_points',
        ]) {
            delete data[field];
        }
    }

    private startOverlayAnimation() {
        if (this.overlayAnimationFrame !== null) return;
        const animate = (timestamp: number) => {
            this.overlayAnimationFrame = null;
            const duration = this.overlayTransitionDurationMs;
            const progress = duration <= 0
                ? 1
                : this.clamp(
                    (timestamp - this.overlayTransitionStartedAt) / duration,
                    0,
                    1,
                );
            // Ease out so the overlay catches a moving person quickly without
            // jumping to each slower server inference result.
            const eased = 1 - Math.pow(1 - progress, 3);
            this.overlayRenderedLandmarks = this.interpolateOverlayPoints(
                this.overlayStartLandmarks,
                this.overlayTargetLandmarks,
                eased,
            );
            this.overlayRenderedDetectorCorners = this.interpolateOverlayPoints(
                this.overlayStartDetectorCorners,
                this.overlayTargetDetectorCorners,
                eased,
            );
            this.overlayRenderedDetectorCenter = this.interpolateOverlayPoint(
                this.overlayStartDetectorCenter,
                this.overlayTargetDetectorCenter,
                eased,
            );
            this.drawCurrentOverlayFrame();

            if (progress < 1) {
                this.overlayAnimationFrame = requestAnimationFrame(animate);
            }
        };
        this.overlayAnimationFrame = requestAnimationFrame(animate);
    }

    private drawCurrentOverlayFrame() {
        const landmarks = this.overlayRenderedLandmarks.length
            ? this.overlayRenderedLandmarks
            : (this.analysis?.landmarks || []);
        const detectorCorners = this.overlayRenderedDetectorCorners.length
            ? this.overlayRenderedDetectorCorners
            : this.detectorCorners;
        const detectorCenter = this.overlayRenderedDetectorCenter
            || this.buckyDetection?.center;
        this.drawOverlay(
            landmarks,
            this.overlayRenderedLandmarks.length
                ? this.overlayPassed
                : Boolean(this.analysis?.pass),
            detectorCorners,
            detectorCenter,
        );
    }

    private cloneOverlayPoint(point: any) {
        if (!point || !Number.isFinite(Number(point.x)) || !Number.isFinite(Number(point.y))) {
            return null;
        }
        return {
            ...point,
            x: Number(point.x),
            y: Number(point.y),
            visibility: point.visibility === undefined
                ? point.visibility
                : Number(point.visibility),
        };
    }

    private cloneOverlayPoints(points: any[]) {
        // Preserve the original landmark indexes (0..32). Removing an
        // invisible slot would connect the wrong joints on the next frame.
        return (points || []).map((point) => this.cloneOverlayPoint(point));
    }

    private interpolateOverlayPoint(start: any, target: any, ratio: number) {
        if (!target) return null;
        if (!start) return this.cloneOverlayPoint(target);
        const visibility = Number.isFinite(Number(target.visibility))
            ? Number(start.visibility ?? target.visibility)
                + (Number(target.visibility) - Number(start.visibility ?? target.visibility)) * ratio
            : target.visibility;
        return {
            ...target,
            x: Number(start.x) + (Number(target.x) - Number(start.x)) * ratio,
            y: Number(start.y) + (Number(target.y) - Number(start.y)) * ratio,
            visibility,
        };
    }

    private interpolateOverlayPoints(start: any[], target: any[], ratio: number) {
        if (!target?.length) return [];
        return target.map((point, index) => this.interpolateOverlayPoint(
            start?.[index],
            point,
            ratio,
        ));
    }

    private overlayPoseChangeIsNegligible(previous: any[], next: any[]) {
        if (!previous?.length || !next?.length) return false;
        if (XRAY_POSTURE_KEY_IDS.some((index) => Boolean(next[index]?.micro_motion))) return false;
        const distances = XRAY_POSTURE_KEY_IDS
            .map((index) => {
                const first = previous[index];
                const second = next[index];
                return this.isVisible(first) && this.isVisible(second)
                    ? this.distance(first, second)
                    : null;
            })
            .filter((value): value is number => value !== null)
            .sort((first, second) => first - second);
        if (!distances.length) return false;
        const middle = Math.floor(distances.length / 2);
        const median = distances.length % 2
            ? distances[middle]
            : (distances[middle - 1] + distances[middle]) / 2;
        const maximum = distances[distances.length - 1];
        return median <= this.overlayStaticMedianDeadband
            && maximum <= this.overlayStaticMaxDeadband;
    }

    private overlayMotionIsDiscontinuous(
        previousLandmarks: any[],
        nextLandmarks: any[],
        previousDetector: any[],
        nextDetector: any[],
    ) {
        const movedTooFar = (first: any, second: any) => Boolean(
            first
            && second
            && this.distance(first, second) > 0.3
        );
        const coreJoints = [11, 12, 23, 24];
        return coreJoints.some((index) => movedTooFar(
            previousLandmarks?.[index],
            nextLandmarks?.[index],
        )) || [0, 1, 2, 3].some((index) => movedTooFar(
            previousDetector?.[index],
            nextDetector?.[index],
        ));
    }

    private drawOverlay(
        landmarks: any[],
        passed: boolean,
        detectorCorners: any[] = this.detectorCorners,
        detectorCenter: any = this.buckyDetection?.center,
    ) {
        const overlay = document.getElementById('xray-overlay') as HTMLCanvasElement;
        const hasCurrent = Boolean(landmarks?.length);
        const hasDetector = detectorCorners?.length === 4;
        if (!overlay || (!hasCurrent && !hasDetector && !this.normalSkeletonEnabled)) {
            this.clearOverlay(false);
            return;
        }

        const rect = overlay.getBoundingClientRect();
        const overlayWidth = Math.max(1, Math.round(rect.width));
        const overlayHeight = Math.max(1, Math.round(rect.height));
        if (overlay.width !== overlayWidth) overlay.width = overlayWidth;
        if (overlay.height !== overlayHeight) overlay.height = overlayHeight;
        const ctx = overlay.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, overlay.width, overlay.height);
        const connections = XRAY_UPPER_BODY_CONNECTIONS;
        const media = this.activeMediaSize();
        const mediaWidth = media.width;
        const mediaHeight = media.height;
        if (!mediaWidth || !mediaHeight) {
            this.clearOverlay(false);
            return;
        }
        const layout = this.mediaLayout(overlay.width, overlay.height, mediaWidth, mediaHeight);
        const px = (point: any) => layout.offsetX + point.x * layout.drawWidth;
        const py = (point: any) => layout.offsetY + point.y * layout.drawHeight;

        if (hasDetector) {
            this.drawDetectorOverlay(ctx, detectorCorners, detectorCenter, px, py);
        }

        if (hasCurrent) {
            this.drawSkeleton(
                ctx,
                landmarks,
                connections,
                px,
                py,
                '#10b981',
                3,
                5
            );
        }

        if (this.normalSkeletonEnabled && this.personalizedSkeleton?.length) {
            this.drawSkeleton(
                ctx,
                this.personalizedSkeleton,
                XRAY_UPPER_BODY_CONNECTIONS,
                px,
                py,
                '#f59e0b',
                3,
                5
            );
            const guideShoulder = this.personalizedSkeleton[11];
            if (this.isVisible(guideShoulder)) {
                ctx.save();
                ctx.fillStyle = '#fbbf24';
                ctx.font = 'bold 12px sans-serif';
                ctx.fillText('NORMAL GUIDE', px(guideShoulder) + 8, py(guideShoulder) - 10);
                ctx.restore();
            }
        }

    }

    private drawDetectorOverlay(
        ctx: CanvasRenderingContext2D,
        corners: any[],
        detectorCenter: any,
        px: (point: any) => number,
        py: (point: any) => number,
    ) {
        const validCorners = corners?.length === 4
            && corners.every((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y));
        if (!validCorners) return;

        const center = Number.isFinite(detectorCenter?.x) && Number.isFinite(detectorCenter?.y)
            ? detectorCenter
            : {
                x: corners.reduce((sum, point) => sum + point.x, 0) / corners.length,
                y: corners.reduce((sum, point) => sum + point.y, 0) / corners.length,
            };
        const color = '#38bdf8';

        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 3;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.setLineDash([]);

        ctx.beginPath();
        ctx.moveTo(px(corners[0]), py(corners[0]));
        for (let index = 1; index < corners.length; index += 1) {
            ctx.lineTo(px(corners[index]), py(corners[index]));
        }
        ctx.closePath();
        ctx.stroke();

        for (const corner of corners) {
            ctx.beginPath();
            ctx.arc(px(corner), py(corner), 4, 0, Math.PI * 2);
            ctx.fill();
        }

        const centerX = px(center);
        const centerY = py(center);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(centerX - 8, centerY);
        ctx.lineTo(centerX + 8, centerY);
        ctx.moveTo(centerX, centerY - 8);
        ctx.lineTo(centerX, centerY + 8);
        ctx.stroke();

        ctx.font = 'bold 12px sans-serif';
        ctx.fillText('BUCKY DETECTOR', px(corners[0]) + 8, py(corners[0]) - 8);
        ctx.restore();
    }

    private clearOverlay(resetTracking: boolean = true) {
        if (resetTracking) {
            if (this.overlayAnimationFrame !== null) {
                cancelAnimationFrame(this.overlayAnimationFrame);
                this.overlayAnimationFrame = null;
            }
            this.overlayStartLandmarks = [];
            this.overlayTargetLandmarks = [];
            this.overlayRenderedLandmarks = [];
            this.overlayStartDetectorCorners = [];
            this.overlayTargetDetectorCorners = [];
            this.overlayRenderedDetectorCorners = [];
            this.overlayStartDetectorCenter = null;
            this.overlayTargetDetectorCenter = null;
            this.overlayRenderedDetectorCenter = null;
            this.overlayTransitionStartedAt = 0;
            this.overlayLastTargetAt = 0;
        }
        const overlay = document.getElementById('xray-overlay') as HTMLCanvasElement;
        if (!overlay) return;
        const ctx = overlay.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, overlay.width, overlay.height);
    }

    private updatePrimarySummary(data: XrayAnalysisResponse | null) {
        const feedback = data?.feedback || [];
        const primary = feedback.filter((item: any) => item.primary);
        const secondary = feedback.filter((item: any) => item.primary === false);
        this.primaryTotalCount = primary.length;
        this.primaryOkCount = primary.filter((item: any) => item.ok).length;
        this.secondaryIssueCount = secondary.filter((item: any) => !item.ok).length;
        this.updateUserFacingSummary(data);
        if (!data) {
            this.displayStatus = '대기 중';
        } else if (data.finalStatus === 'UNAVAILABLE') {
            this.displayStatus = '판정 불가';
        } else if (data.finalStatus === 'BORDERLINE') {
            this.displayStatus = '경계';
        } else if (data.finalStatus === 'CORRECTION_REQUIRED') {
            this.displayStatus = '교정 필요';
        } else if (data.finalStatus === 'NORMAL') {
            this.displayStatus = '정상';
        } else if (!data.pass) {
            this.displayStatus = 'FAIL';
        } else {
            this.displayStatus = 'PASS';
        }
    }

    public async toggleDetailAnalysis() {
        this.detailAnalysisOpen = !this.detailAnalysisOpen;
        await this.service.render();
    }

    public async toggleModelDetails() {
        this.modelDetailsOpen = !this.modelDetailsOpen;
        await this.service.render();
    }

    private updateUserFacingSummary(data: XrayAnalysisResponse | null) {
        if (!data) {
            this.userSummaryMessage = '측정을 시작하면 자세 안내가 표시됩니다.';
            this.primaryActionItems = [];
            return;
        }
        if (this.isBuckyNotDetected(data)) {
            this.userSummaryMessage = '촬영판을 인식하지 못해 자세를 판정할 수 없습니다.';
            this.primaryActionItems = [{
                label: '다시 자동 분석',
                message: '다시 촬영할 때 처음 1~2초는 촬영판의 위·아래·좌·우 테두리가 보이도록 한 걸음 떨어져 서고, 파란 촬영판 표시가 나타난 뒤 천천히 촬영 자세를 잡아 주세요.',
            }];
            return;
        }
        if (!data.landmarks?.length && data.score === 0) {
            const reason = String(data?.reason || data?.error || '');
            const poseConfidenceIssue = reason.startsWith('pose_')
                || reason.startsWith('shoulder_')
                || reason.startsWith('yolo_pose');
            this.userSummaryMessage = poseConfidenceIssue
                ? '상체와 양팔은 보이지만 자세 관절 인식 신뢰도가 부족합니다.'
                : (String(data?.message || '').trim()
                    || '자세 관절을 충분히 인식하지 못했습니다.');
            this.primaryActionItems = [{
                label: '다시 분석',
                message: '현재 구도를 유지하고 조명과 흔들림을 확인한 뒤 다시 측정해 주세요.',
            }];
            return;
        }
        if (data.finalStatus === 'UNAVAILABLE') {
            const reasons = data.unavailableReasons || data.normalizedFeatures?.unavailableReasons || [];
            const detectorConfidenceIssue = reasons.includes('detector_confidence_low')
                || reasons.includes('detector_confidence_unavailable');
            this.userSummaryMessage = detectorConfidenceIssue
                ? '디텍터는 찾았지만 자동 인식 신뢰도가 판정 기준에 미치지 못했습니다.'
                : '인식 신뢰도가 부족해 자세를 확정할 수 없습니다.';
            this.primaryActionItems = [{
                label: detectorConfidenceIssue ? '다시 자동 분석' : '직접 확인',
                message: detectorConfidenceIssue
                    ? '자동 디텍터가 촬영판 전체를 인식할 수 있는 이미지를 다시 업로드해 주세요.'
                    : '현재 구도와 조명을 확인한 뒤 다시 측정해 주세요.',
            }];
            return;
        }
        if (data.finalStatus === 'BORDERLINE') {
            const actions = this.buildDetailedCorrectionActions(data);
            this.userSummaryMessage = actions.length
                ? '경계 상태입니다. 아래 측정 항목을 조정한 뒤 자세를 잠시 유지해 주세요.'
                : '경계 상태입니다. 자세를 잠시 유지하고 다시 확인해 주세요.';
            this.primaryActionItems = actions;
            return;
        }
        if (data.finalStatus === 'CORRECTION_REQUIRED') {
            const actions = this.buildDetailedCorrectionActions(data);
            this.userSummaryMessage = '자세를 조정해야 합니다. 아래 항목을 순서대로 확인해 주세요.';
            this.primaryActionItems = actions.length
                ? actions
                : [{
                    label: '교정 안내',
                    message: data.correctionMessage || '양쪽 어깨와 몸 중심, 촬영 범위를 다시 확인해 주세요.',
                }];
            return;
        }
        if (this.isLargePoseMismatch(data)) {
            const actions = this.buildDetailedCorrectionActions(data);
            this.userSummaryMessage = '촬영 기준과 다른 항목을 아래 순서대로 조정해 주세요.';
            this.primaryActionItems = actions.length ? actions : [
                {
                    label: '어깨 위치',
                    message: '양쪽 어깨가 모두 촬영판 안에 보이도록 서고, 좌우 어깨 높이를 수평으로 맞춰 주세요.',
                },
                {
                    label: '몸 중심',
                    message: '등을 촬영판 정면에 붙이고 몸 중심을 촬영판 중앙 표시와 맞춰 주세요.',
                },
                {
                    label: '팔 위치',
                    message: '양쪽 팔꿈치를 같은 높이로 벌리고 손은 자연스럽게 아래로 내려 주세요.',
                },
            ];
            return;
        }
        if (data.pass) {
            this.userSummaryMessage = '좋습니다. 자세가 촬영 기준에 맞습니다.';
            this.primaryActionItems = [];
            return;
        }
        const issues = (data.feedback || [])
            .filter((item: any) => item && item.ok === false)
            .sort((a: any, b: any) => {
                const primaryDelta = (b.primary ? 1 : 0) - (a.primary ? 1 : 0);
                if (primaryDelta) return primaryDelta;
                return Number(b.z_score || 0) - Number(a.z_score || 0);
            })
            .slice(0, 2)
            .map((item: any) => ({
                label: this.easyFeatureLabel(item),
                message: String(item?.message || '').trim() || this.userActionMessage(item),
            }));
        this.userSummaryMessage = issues.length ? '자세를 조금 조정해야 합니다.' : '자세를 다시 한 번 확인해주세요.';
        this.primaryActionItems = issues;
    }

    private buildDetailedCorrectionActions(data: any, limit: number = 3) {
        const actions: any[] = [];
        const labels = new Set<string>();
        const feedbackIssues = (data?.feedback || [])
            .filter((item: any) => item && item.ok === false)
            .sort((a: any, b: any) => {
                const primaryDelta = (b.primary ? 1 : 0) - (a.primary ? 1 : 0);
                if (primaryDelta) return primaryDelta;
                const priorityDelta = Number(a.priority ?? 99) - Number(b.priority ?? 99);
                if (priorityDelta) return priorityDelta;
                return Number(b.z_score || 0) - Number(a.z_score || 0);
            });
        for (const item of feedbackIssues) {
            const label = this.easyFeatureLabel(item);
            if (labels.has(label)) continue;
            const message = String(item?.message || '').trim() || this.userActionMessage(item);
            actions.push({ label, message });
            labels.add(label);
            if (actions.length >= limit) return actions;
        }

        const postureClasses = (data?.posture_error_assessment?.classes || [])
            .filter((item: any) => item?.state === 'abnormal' || item?.state === 'suspected')
            .sort((a: any, b: any) => Number(a?.class_index || 99) - Number(b?.class_index || 99));
        for (const item of postureClasses) {
            const label = String(item?.title || `자세 판정 ${item?.class_index || ''}`).trim();
            if (labels.has(label)) continue;
            const metric = this.postureMetricMessage(item);
            const action = this.postureUserMessage(item);
            actions.push({ label, message: `${metric} ${action}`.trim() });
            labels.add(label);
            if (actions.length >= limit) return actions;
        }
        return actions;
    }

    private isLargePoseMismatch(data: any) {
        if (!data || data.pass) return false;
        if (data.large_pose_mismatch) return true;
        const primaryIssues = (data.feedback || []).filter((item: any) => item?.primary && item.ok === false);
        const maxZ = primaryIssues.reduce((max: number, item: any) => Math.max(max, Number(item?.z_score || 0)), 0);
        const majorCount = primaryIssues.filter((item: any) => item?.severity === 'major').length;
        return Number(data.score || 0) <= 60 || primaryIssues.length >= 3 || majorCount >= 2 || maxZ >= 6;
    }

    private easyFeatureLabel(item: any) {
        const labels: any = {
            shoulder_tilt_abs: '어깨 수평',
            shoulder_center_y: '촬영판 높이',
            torso_center_offset: '상체 중심',
            torso_tilt_avg: '몸통 기울기',
            body_center_offset: '몸 위치',
            torso_tilt_asymmetry: '좌우 균형',
            armpit_opening_angle: '팔 벌림',
            upper_arm_abduction: '팔 벌림',
            elbow_outward_offset: '팔꿈치 위치',
            wrist_inward_return: '손 위치',
            left_elbow_angle: '왼쪽 팔',
            right_elbow_angle: '오른쪽 팔',
            elbow_bend_shape: '팔 굽힘',
            left_arm_inward_return: '왼쪽 손 위치',
            right_arm_inward_return: '오른쪽 손 위치',
        };
        return labels[item?.name] || item?.label || '자세 조정';
    }

    private userActionMessage(item: any) {
        const name = item?.name;
        const direction = item?.direction;
        if (name === 'body_center_offset') {
            return direction === 'right' ? '몸을 오른쪽으로 조금 이동해주세요.' : '몸을 왼쪽으로 조금 이동해주세요.';
        }
        if (name === 'torso_center_offset') return '상체 중심을 몸 중심과 맞춰주세요.';
        if (name === 'shoulder_tilt_abs') return '어깨를 수평으로 맞춰주세요.';
        if (name === 'torso_tilt_avg') return '몸통을 촬영판에 곧게 맞춰주세요.';
        if (name === 'torso_tilt_asymmetry') return '몸통이 한쪽으로 틀어지지 않게 정면을 맞춰주세요.';
        if (['armpit_opening_angle', 'upper_arm_abduction', 'elbow_outward_offset'].includes(name)) return '팔꿈치를 바깥쪽으로 조금 벌려주세요.';
        if (['wrist_inward_return', 'left_arm_inward_return', 'right_arm_inward_return'].includes(name)) return '손은 자연스럽게 아래로 내려주세요.';
        if (['left_elbow_angle', 'right_elbow_angle', 'elbow_bend_shape'].includes(name)) return '팔꿈치를 자연스럽게 굽혀주세요.';
        return item?.message || '자세를 조금 조정해주세요.';
    }

    public postureMetricMessage(item: any) {
        const classIndex = Number(item?.class_index);
        const metrics = item?.classifier_check?.metrics || {};
        const evidence = item?.evidence || {};
        if (classIndex === 1) {
            const angle = this.metricNumber(metrics.shoulderAngleDeg);
            const height = this.metricPercent(metrics.shoulderHeightDiffRatio);
            const margin = this.metricPercent(
                metrics.minimumHorizontalBoundaryMarginRatio
            );
            return [
                angle === null ? null : `어깨 각도 ${Math.abs(angle).toFixed(1)}°`,
                height === null ? null : `좌우 높이 차 ${height}`,
                margin === null ? null : `촬영 경계 여유 ${margin}`,
            ].filter(Boolean).join(' · ') || '어깨 측정값을 계산하는 중입니다.';
        }
        if (classIndex === 2) {
            const center = this.metricNumber(metrics.centerOffsetRatio);
            const tilt = this.metricPercent(metrics.torsoTiltRatio);
            return [
                center === null
                    ? null
                    : `사람 중심이 디텍터 중심보다 ${center < 0 ? '왼쪽' : center > 0 ? '오른쪽' : '가운데'} ${this.metricPercent(Math.abs(center))}`,
                tilt === null ? null : `몸통 기울기 ${tilt}`,
            ].filter(Boolean).join(' · ') || '사람·디텍터 중심 차이를 계산하는 중입니다.';
        }
        if (classIndex === 3) {
            return '턱·폐첨부 가림과 실제 밀착 거리는 단일 RGB 영상에서 자동 측정할 수 없습니다.';
        }
        if (classIndex === 4) {
            const offset = this.metricNumber(metrics.horizontalOffsetRatio);
            const coverage = this.metricPercent(metrics.chestRoiCoverageRatio);
            const margins = [
                metrics.leftChestMarginRatio,
                metrics.rightChestMarginRatio,
                metrics.chestTopMarginRatio,
                metrics.estimatedChestBottomMarginRatio,
            ]
                .map((value: any) => this.metricNumber(value))
                .filter((value: number | null): value is number => value !== null);
            const minimumMargin = margins.length
                ? this.metricPercent(Math.min(...margins))
                : null;
            return [
                offset === null
                    ? null
                    : `흉부 중심이 디텍터 중심보다 ${offset < 0 ? '왼쪽' : offset > 0 ? '오른쪽' : '가운데'} ${this.metricPercent(Math.abs(offset))}`,
                coverage === null ? null : `촬영 범위 포함 ${coverage}`,
                minimumMargin === null ? null : `최소 경계 여유 ${minimumMargin}`,
            ].filter(Boolean).join(' · ') || '흉부 촬영 범위를 계산하는 중입니다.';
        }
        return '실시간 측정값을 계산하는 중입니다.';
    }

    public postureUserMessage(item: any) {
        const classIndex = Number(item?.class_index);
        if (item?.state === 'not_evaluated') {
            return classIndex === 3
                ? '촬영자가 턱 위치와 디텍터 밀착 여부를 직접 확인해 주세요.'
                : '관절 또는 디텍터 인식 신뢰도가 부족해 판정할 수 없습니다.';
        }
        const normalMessages: Record<number, string> = {
            1: '어깨 기울기와 촬영 범위가 정상 기준 안입니다.',
            2: '사람 중심과 디텍터 중심 차이가 정상 기준 안입니다.',
            3: '턱 위치와 밀착 여부를 직접 확인해 주세요.',
            4: '흉부 중심과 촬영 범위가 정상 기준 안입니다.',
        };
        const correctionMessages: Record<number, string> = {
            1: '양쪽 어깨 높이와 촬영 범위를 다시 맞춰 주세요.',
            2: '사람 중심이 디텍터 중심에 가까워지도록 몸통 위치를 조정해 주세요.',
            3: '턱을 들고 디텍터에 충분히 밀착했는지 직접 확인해 주세요.',
            4: '흉부 전체가 디텍터 촬영 범위 안에 들어오도록 위치를 조정해 주세요.',
        };
        if (item?.state === 'normal') return normalMessages[classIndex] || '정상 기준 안입니다.';
        if (item?.state === 'suspected') {
            return `${correctionMessages[classIndex] || '자세를 조정해 주세요.'} 현재 값은 경계 구간입니다.`;
        }
        return correctionMessages[classIndex] || '자세를 조정해 주세요.';
    }

    private metricNumber(value: any): number | null {
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
    }

    private metricPercent(value: any): string | null {
        const number = this.metricNumber(value);
        return number === null ? null : `${(Math.abs(number) * 100).toFixed(1)}%`;
    }

    private async playVoice(data: XrayAnalysisResponse, options: { adaptive?: boolean } = {}) {
        if (!this.voiceEnabled) return;
        const adaptive = Boolean(options.adaptive);
        if (!adaptive && this.voicePlayed) return;
        const voice = data?.voice;
        const baseText = (voice?.text || '').trim();
        if (!baseText) return;
        const key = voice?.key || baseText;
        const now = Date.now();
        if (adaptive && this.shouldSkipAdaptiveVoice(data, key, now)) return;
        if (!adaptive && key === this.lastVoiceKey && now - this.lastVoiceAt < this.voiceRepeatDelayMs) return;
        if (this.voiceBusy && !adaptive) return;

        const text = adaptive ? this.adaptiveVoiceText(data, baseText, key) : baseText;
        if (!text) return;

        this.lastVoiceKey = key;
        this.lastVoiceAt = now;
        if (adaptive) {
            this.lastAdaptiveVoiceKey = key;
            this.lastAdaptiveVoiceAt = now;
            this.lastAdaptiveVoiceWasPass = Boolean(data?.pass);
        }
        this.voiceMessage = text;
        this.voiceError = '';
        const cacheKey = this.voiceCacheKey(key, text, baseText);
        const canUseServerAudio = cacheKey === key;

        if (adaptive) {
            const browserStarted = await this.speakWithBrowser(text);
            this.voicePlayed = browserStarted;
            this.voiceBusy = false;
            if (browserStarted) {
                this.voiceError = '';
            } else {
                this.voiceError = '브라우저 음성 안내가 차단되어 MeloTTS 음성으로 재시도합니다.';
                this.voiceBusy = true;
                await this.service.render();
                await this.fetchAndPlayVoiceAudio(cacheKey, text);
            }
            await this.service.render();
            return;
        }

        if (canUseServerAudio && voice?.audio) {
            this.voiceCache.set(key, voice.audio);
            this.voicePlayed = true;
            this.playAudioData(voice.audio, text);
            await this.service.render();
            return;
        }

        const cached = this.voiceCache.get(cacheKey);
        if (cached) {
            this.voicePlayed = true;
            this.playAudioData(cached, text);
            await this.service.render();
            return;
        }

        const browserStarted = await this.speakWithBrowser(text);
        if (browserStarted) {
            this.voicePlayed = true;
            this.voiceBusy = true;
            this.cacheVoiceAudio(cacheKey, text);
            await this.service.render();
            return;
        }

        this.voiceBusy = true;
        await this.service.render();
        try {
            const timeout = new Promise<any>((resolve) => setTimeout(() => resolve({
                code: 408,
                data: { fallback: true, message: 'MeloTTS 생성이 지연되어 브라우저 음성으로 안내합니다.' }
            }), 3500));
            const { code, data: audio } = await Promise.race([
                wiz.call('tts', { text, key: cacheKey }),
                timeout,
            ]);
            if (code === 200 && audio?.audio) {
                this.voiceCache.set(cacheKey, audio.audio);
                this.voicePlayed = true;
                this.playAudioData(audio.audio, text);
            } else if (audio?.fallback) {
                this.voicePlayed = await this.speakWithBrowser(text);
                this.voiceError = this.voicePlayed ? '' : '브라우저 음성 안내를 시작할 수 없습니다.';
            } else {
                this.voiceError = audio?.error || audio?.message || '음성 안내를 생성할 수 없습니다.';
                this.voicePlayed = await this.speakWithBrowser(text);
            }
        } catch (error: any) {
            this.voiceError = error?.responseJSON?.error || error?.responseJSON?.message || '음성 안내를 생성할 수 없습니다.';
            this.voicePlayed = await this.speakWithBrowser(text);
        } finally {
            this.voiceBusy = false;
            await this.service.render();
        }
    }

    private shouldSkipAdaptiveVoice(data: XrayAnalysisResponse, key: string, now: number) {
        if (!key) return true;
        const temporal = data?.temporalStabilization;
        if (temporal && temporal.stable !== true) return true;
        if (!this.lastAdaptiveVoiceKey) return false;
        if (key !== this.lastAdaptiveVoiceKey) return false;

        if (Boolean(data?.pass) && !this.lastAdaptiveVoiceWasPass) return false;
        if (Boolean(data?.pass)) return true;
        return now - this.lastAdaptiveVoiceAt < this.feedbackRealtimeDelayMs;
    }

    private adaptiveVoiceText(data: XrayAnalysisResponse, baseText: string, key: string) {
        const becamePass = Boolean(data?.pass) && !this.lastAdaptiveVoiceWasPass;
        if (becamePass) return '좋습니다. 자세가 맞았습니다. 그대로 유지해주세요.';
        if (this.lastAdaptiveVoiceKey && key !== this.lastAdaptiveVoiceKey && !data?.pass && !this.isLargePoseMismatch(data)) {
            return `좋습니다. 이제 ${baseText}`;
        }
        return baseText;
    }

    private resetAdaptiveVoiceState() {
        this.lastAdaptiveVoiceKey = '';
        this.lastAdaptiveVoiceAt = 0;
        this.lastAdaptiveVoiceWasPass = false;
        this.lastBrowserSpeechText = '';
        this.lastBrowserSpeechAt = 0;
        this.postureVoteHistory = [];
    }

    private applyRealtimePostureStabilization(data: XrayAnalysisResponse) {
        const status = data?.finalStatus;
        const className = data?.finalClass;
        if (status && status !== 'UNAVAILABLE' && className) {
            this.postureVoteHistory.push({ status, className });
            if (this.postureVoteHistory.length > this.postureVotingWindowSize) {
                this.postureVoteHistory.shift();
            }
        }
        const votes = this.postureVoteHistory.map((row) => row.className);
        const counts = votes.reduce((result: Record<string, number>, name: PostureFinalClass) => {
            result[name] = (result[name] || 0) + 1;
            return result;
        }, {} as Record<string, number>);
        const entries = Object.entries(counts) as [PostureFinalClass, number][];
        const top = entries.length ? Math.max(...entries.map((row) => row[1])) : 0;
        const winners = entries.filter((row) => row[1] === top).map((row) => row[0]);
        const stable = votes.length >= this.postureMinimumValidFrames
            && winners.length === 1
            && top >= this.postureMinimumValidFrames;
        const temporalStabilization: TemporalPostureResult = {
            method: 'recent_valid_frame_majority_v1',
            validFrameCount: votes.length,
            votingWindowSize: this.postureVotingWindowSize,
            stable,
            tie: winners.length > 1,
            finalClass: stable ? winners[0] : null,
            finalStatus: stable
                ? (winners[0] === 'NORMAL' ? 'NORMAL' : 'CORRECTION_REQUIRED')
                : votes.length >= this.postureMinimumValidFrames
                    ? 'BORDERLINE'
                    : 'UNAVAILABLE'
        };
        data.temporalStabilization = temporalStabilization;
        data.validFrameCount = votes.length;
        data.votingWindowSize = this.postureVotingWindowSize;
        if (stable) {
            data.finalClass = winners[0];
            data.finalStatus = temporalStabilization.finalStatus;
        }
    }

    private voiceCacheKey(key: string, text: string, baseText: string) {
        if (text === baseText) return key;
        return `${key}:adaptive:${this.textHash(text)}`;
    }

    private textHash(text: string) {
        let hash = 0;
        for (let index = 0; index < text.length; index += 1) {
            hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
        }
        return Math.abs(hash).toString(36);
    }

    private async cacheVoiceAudio(key: string, text: string) {
        try {
            const { code, data: audio } = await wiz.call('tts', { text, key });
            if (code === 200 && audio?.audio) {
                this.voiceCache.set(key, audio.audio);
            }
        } catch {
            // Browser speech already started; MeloTTS cache failures should not delay guidance.
        } finally {
            this.voiceBusy = false;
            await this.service.render();
        }
    }

    private async fetchAndPlayVoiceAudio(key: string, text: string) {
        try {
            const { code, data: audio } = await wiz.call('tts', { text, key });
            if (code === 200 && audio?.audio) {
                this.voiceCache.set(key, audio.audio);
                this.voicePlayed = true;
                this.playAudioData(audio.audio, text);
                this.voiceError = '';
            } else {
                this.voicePlayed = false;
                this.voiceError = audio?.error || audio?.message || '음성 안내를 재생할 수 없습니다. 브라우저 음성 권한과 시스템 음량을 확인해 주세요.';
            }
        } catch (error: any) {
            this.voicePlayed = false;
            this.voiceError = error?.responseJSON?.error || error?.responseJSON?.message || '음성 안내를 재생할 수 없습니다.';
        } finally {
            this.voiceBusy = false;
        }
    }

    private shouldRequestVoiceAudio() {
        return this.voiceEnabled && !this.voicePlayed && this.calibrationState !== 'calibrating';
    }

    private playAudioData(audio: string, fallbackText: string = '') {
        window?.speechSynthesis?.cancel();
        if (!this.voiceAudio) this.voiceAudio = new Audio();
        this.voiceAudio.pause();
        this.voiceAudio.currentTime = 0;
        this.voiceAudio.muted = false;
        this.voiceAudio.volume = 1;
        this.voiceAudio.src = audio;
        this.voiceAudio.load();
        this.voiceAudio.play().catch(() => {
            this.speakWithBrowser(fallbackText).then((fallbackStarted) => {
                this.voiceError = fallbackStarted ? '' : '브라우저에서 음성 재생이 차단되었습니다.';
                this.service.render();
            });
        });
    }

    private async speakWithBrowser(text: string) {
        const synth = window?.speechSynthesis;
        if (!synth || !text) return false;
        try {
            this.unlockVoiceOutput();
            const now = Date.now();
            if (text === this.lastBrowserSpeechText && now - this.lastBrowserSpeechAt < 1800) {
                return true;
            }
            this.lastBrowserSpeechText = text;
            this.lastBrowserSpeechAt = now;
            synth.cancel();
            synth.resume();
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'ko-KR';
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            const voices = synth.getVoices?.() || [];
            const koreanVoice = voices.find((voice) => /ko(-|_)KR/i.test(voice.lang || ''))
                || voices.find((voice) => /^ko/i.test(voice.lang || ''));
            if (koreanVoice) utterance.voice = koreanVoice;
            let started = false;
            const startedPromise = new Promise<boolean>((resolve) => {
                const timer = setTimeout(() => resolve(started), 800);
                utterance.onstart = () => {
                    started = true;
                    clearTimeout(timer);
                    resolve(true);
                };
                utterance.onerror = () => {
                    clearTimeout(timer);
                    resolve(false);
                };
            });
            synth.speak(utterance);
            return await startedPromise;
        } catch {
            return false;
        }
    }

    private unlockVoiceOutput() {
        try {
            this.unlockVoiceAudioElement();
            const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!AudioContextClass) return;
            const audioContext: AudioContext = this.audioContext || new AudioContextClass();
            this.audioContext = audioContext;
            audioContext.resume?.();
            if (this.voiceUnlocked) return;
            const oscillator = audioContext.createOscillator();
            const gain = audioContext.createGain();
            gain.gain.value = 0.0001;
            oscillator.connect(gain);
            gain.connect(audioContext.destination);
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.03);
            this.voiceUnlocked = true;
        } catch {
            // Some browsers block AudioContext priming; speech and MeloTTS fallbacks handle playback.
        }
    }

    private unlockVoiceAudioElement() {
        if (this.voiceAudioUnlocked) return;
        try {
            if (!this.voiceAudio) this.voiceAudio = new Audio();
            this.voiceAudio.src = this.silentAudioData;
            this.voiceAudio.muted = true;
            this.voiceAudio.volume = 0;
            const playResult = this.voiceAudio.play();
            if (playResult?.then) {
                playResult
                    .then(() => {
                        if (!this.voiceAudio) return;
                        this.voiceAudio.pause();
                        this.voiceAudio.currentTime = 0;
                        this.voiceAudio.muted = false;
                        this.voiceAudio.volume = 1;
                        this.voiceAudioUnlocked = true;
                    })
                    .catch(() => {
                        this.voiceAudioUnlocked = false;
                    });
            } else {
                this.voiceAudio.pause();
                this.voiceAudio.currentTime = 0;
                this.voiceAudio.muted = false;
                this.voiceAudio.volume = 1;
                this.voiceAudioUnlocked = true;
            }
        } catch {
            this.voiceAudioUnlocked = false;
        }
    }

    private stopVoiceAudio() {
        window?.speechSynthesis?.cancel();
        if (this.voiceAudio) {
            this.voiceAudio.pause();
            this.voiceAudio.currentTime = 0;
            this.voiceAudio.src = '';
        }
        this.voiceAudioUnlocked = false;
    }

    private loadPreviewImageSize(src: string) {
        return new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
                this.previewImageWidth = img.naturalWidth || 1;
                this.previewImageHeight = img.naturalHeight || 1;
                resolve();
            };
            img.onerror = () => {
                this.previewImageWidth = 1;
                this.previewImageHeight = 1;
                resolve();
            };
            img.src = src;
        });
    }

    private waitForAnimationFrame() {
        return new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }

    private revokeUploadObjectUrl() {
        if (!this.uploadObjectUrl) return;
        URL.revokeObjectURL(this.uploadObjectUrl);
        this.uploadObjectUrl = '';
        this.previewImageWidth = 0;
        this.previewImageHeight = 0;
    }

    private applyDetectorStatus(data: any, force: boolean = false) {
        if (this.detectorCalibrationMode && !force) return;

        const system = data?.detector_coordinate_system || {};
        const corners = system?.corners || {};
        this.detectorCalibrated = Boolean(data?.detector_calibrated || system?.calibrated);
        this.detectorCorners = this.detectorCalibrated
            ? ['top_left', 'top_right', 'bottom_right', 'bottom_left']
                .map((name) => corners[name])
                .filter((point) => point && Number.isFinite(Number(point.x)) && Number.isFinite(Number(point.y)))
            : [];
        if (this.detectorCalibrated && this.detectorCorners.length === 4) {
            this.detectorLockState = 'locked';
            this.detectorLockSampleCount = this.detectorLockRequiredFrames;
        } else if (!this.detectorLockCandidates.length) {
            this.detectorLockState = 'searching';
            this.detectorLockSampleCount = 0;
        }
        const warning = system?.calibration_warning;
        this.detectorMessage = (
            !this.cameraOn && !this.buckyDetection && !this.detectorCalibrationMode
        )
            ? '실시간 카메라에서 학습 디텍터가 촬영판을 자동 인식합니다.'
            : warning?.message
                ? this.detectorTerminology(warning.message)
                : (this.detectorCalibrated
                    ? '디텍터 ㄱ자 마커 기준 좌표계 적용 중'
                    : '디텍터 기준 좌표계: 화면 중심 proxy 사용');
    }

    private applyBuckyDetection(detection: any) {
        const detectorConfig = detection?.detector_config || {};
        const corners = detection?.corners
            || detectorConfig?.corners
            || detection?.bucky_bbox
            || {};
        const center = detection?.center || detectorConfig?.center_cross || null;
        this.buckyDetection = detection
            ? { ...detection, corners, center }
            : null;
        if (!detection?.detected) {
            this.detectorCalibrated = false;
            this.detectorCorners = [];
            this.detectorMessage = this.detectorTerminology(
                detection?.warning
                || detection?.message
                || '디텍터 촬영판을 자동으로 찾지 못했습니다.'
            );
            return;
        }
        const ordered = ['top_left', 'top_right', 'bottom_right', 'bottom_left']
            .map((name) => corners[name])
            .filter((point) => point && Number.isFinite(Number(point.x)) && Number.isFinite(Number(point.y)));
        if (ordered.length !== 4) return;
        this.detectorCorners = ordered;
        this.detectorCalibrated = true;
        this.detectorLockState = 'locked';
        this.detectorLockSampleCount = this.detectorLockRequiredFrames;
        this.detectorCalibrationMode = false;
        const confidence = Math.round(Number(detection.confidence || 0) * 100);
        const centerSource = detection?.center?.source === 'image_cross_line_refinement'
            ? '영상 + 선 보정'
            : '사각형 중심 계산';
        this.detectorMessage = this.realtime
            ? `디텍터 실시간 추적 ${confidence}% · 중앙 + ${centerSource}`
            : `디텍터 자동 인식 ${confidence}% · 중앙 + ${centerSource} · 이 사진에만 적용`;
    }

    private isBuckyNotDetected(data: any) {
        const detection = data?.bucky_detection || data?.bucky || {};
        return data?.status === 'BUCKY_NOT_DETECTED'
            || (data?.judgement === 'UNAVAILABLE' && data?.detected === false)
            || detection?.status === 'BUCKY_NOT_DETECTED'
            || detection?.detected === false;
    }

    private async saveDetectorCalibration() {
        if (this.detectorCorners.length !== 4) {
            this.detectorMessage = `디텍터 보정은 네 꼭짓점이 모두 필요합니다. 현재 ${this.detectorCorners.length}/4개가 지정되었습니다.`;
            await this.service.render();
            return;
        }
        const submittedCorners = this.detectorCorners.map((point) => ({ x: point.x, y: point.y }));
        const [topLeft, topRight, bottomRight, bottomLeft] = submittedCorners;
        if (this.pendingUploadFile) {
            this.detectorCorners = submittedCorners;
            this.buckyDetection = null;
            this.detectorCalibrated = true;
            this.detectorCalibrationMode = false;
            this.detectorMessage = '수동 디텍터 네 모서리를 이 사진에만 적용합니다.';
            await this.service.render();
            await this.analyzePendingUpload(false);
            return;
        }
        this.detectorCornerSaving = true;
        try {
            const { code, data } = await wiz.call('status_v2', {
                detector_action: 'save',
                config: JSON.stringify({
                    corners: {
                        top_left: topLeft,
                        top_right: topRight,
                        bottom_right: bottomRight,
                        bottom_left: bottomLeft,
                    }
                }),
            });
            if (code === 200) {
                this.applyDetectorStatus(data, true);
                this.detectorCorners = submittedCorners;
                this.detectorCalibrationMode = false;
                this.detectorMessage = data?.calibration_warning?.message || 'ㄱ자 마커 네 꼭짓점 저장 완료. 파란 점선 박스가 마커 4개를 정확히 연결하는지 확인해 주세요.';
                if (this.pendingUploadFile) {
                    await this.service.render();
                    await this.analyzePendingUpload();
                    return;
                }
            } else {
                this.detectorMessage = data?.error
                    ? this.detectorTerminology(data.error)
                    : '디텍터 좌표 저장에 실패했습니다.';
            }
        } finally {
            this.detectorCornerSaving = false;
        }
        requestAnimationFrame(() => this.drawOverlay(this.analysis?.landmarks || [], this.analysis?.pass));
        await this.service.render();
    }

    private eventToMediaPoint(event: MouseEvent) {
        const overlay = document.getElementById('xray-overlay') as HTMLCanvasElement;
        const target = (overlay || event.currentTarget) as HTMLElement;
        const rect = target.getBoundingClientRect();
        const layout = this.currentMediaLayout(rect.width, rect.height);
        if (!layout) return null;
        const x = event.clientX - rect.left - layout.offsetX;
        const y = event.clientY - rect.top - layout.offsetY;
        if (x < 0 || y < 0 || x > layout.drawWidth || y > layout.drawHeight) return null;
        return {
            x: this.clamp(x / layout.drawWidth, 0, 1),
            y: this.clamp(y / layout.drawHeight, 0, 1),
        };
    }

    private currentMediaLayout(containerWidth: number, containerHeight: number) {
        const media = this.activeMediaSize();
        const mediaWidth = media.width;
        const mediaHeight = media.height;
        if (!mediaWidth || !mediaHeight || !containerWidth || !containerHeight) return null;
        return this.mediaLayout(containerWidth, containerHeight, mediaWidth, mediaHeight);
    }

    private mediaLayout(containerWidth: number, containerHeight: number, mediaWidth: number, mediaHeight: number) {
        const mediaRatio = mediaWidth / mediaHeight;
        const containerRatio = containerWidth / containerHeight;
        let drawWidth = containerWidth;
        let drawHeight = containerHeight;
        let offsetX = 0;
        let offsetY = 0;
        if (containerRatio > mediaRatio) {
            drawHeight = containerHeight;
            drawWidth = drawHeight * mediaRatio;
            offsetX = (containerWidth - drawWidth) / 2;
        } else {
            drawWidth = containerWidth;
            drawHeight = drawWidth / mediaRatio;
            offsetY = (containerHeight - drawHeight) / 2;
        }
        return { drawWidth, drawHeight, offsetX, offsetY };
    }

    private activeMediaSize() {
        const preview = document.getElementById('xray-preview-image') as HTMLImageElement;
        if (this.previewImageSrc && !this.realtime) {
            return {
                width: preview?.naturalWidth || this.previewImageWidth,
                height: preview?.naturalHeight || this.previewImageHeight,
                source: 'preview',
            };
        }

        const result = document.getElementById('xray-result-image') as HTMLImageElement;
        if (this.resultImage && !this.realtime) {
            return {
                width: result?.naturalWidth || this.previewImageWidth,
                height: result?.naturalHeight || this.previewImageHeight,
                source: 'result',
            };
        }

        const video = document.getElementById('xray-video') as HTMLVideoElement;
        return {
            width: video?.videoWidth || 0,
            height: video?.videoHeight || 0,
            source: 'video',
        };
    }

    private drawSkeleton(
        ctx: CanvasRenderingContext2D,
        landmarks: any[],
        connections: number[][],
        px: (point: any) => number,
        py: (point: any) => number,
        color: string,
        lineWidth: number,
        radius: number
    ) {
        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);

        for (const [a, b] of connections) {
            const pa = landmarks[a];
            const pb = landmarks[b];
            if (!this.isVisible(pa) || !this.isVisible(pb)) continue;
            ctx.beginPath();
            ctx.moveTo(px(pa), py(pa));
            ctx.lineTo(px(pb), py(pb));
            ctx.stroke();
        }

        ctx.setLineDash([]);
        const pointIndexes = XRAY_POSTURE_KEY_IDS;
        for (const idx of pointIndexes) {
            const point = landmarks[idx];
            if (!this.isVisible(point)) continue;
            ctx.beginPath();
            ctx.arc(px(point), py(point), radius, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    }

    private startCalibration() {
        this.calibrationState = 'calibrating';
        this.calibrationStartedAt = Date.now();
        this.calibrationProgress = 0;
        this.calibrationSamples = [];
        this.personalizedSkeleton = [];
        this.adaptiveMessage = '3초 동안 어깨, 골반, 팔 위치를 평균내 현재 체형에 맞는 정상 자세 가이드를 만드는 중입니다.';
    }

    private updateRealtimeCalibration(landmarks: any[]) {
        if (this.calibrationState !== 'calibrating') return false;

        const elapsed = Date.now() - this.calibrationStartedAt;
        this.calibrationProgress = Math.min(100, Math.round((elapsed / this.calibrationDurationMs) * 100));

        if (this.hasCalibrationJoints(landmarks)) {
            this.calibrationSamples.push(this.cloneLandmarks(landmarks));
            const averaged = this.averageLandmarks(this.calibrationSamples);
            this.personalizedSkeleton = this.buildPersonalizedTargetSkeleton(averaged);
            this.adaptiveMessage = `현재 사람 위치에 정상 자세 가이드를 정렬하는 중입니다. ${this.calibrationSamples.length}개 프레임 평균`;
        }

        if (elapsed < this.calibrationDurationMs) {
            this.displayStatus = '캘리브레이션';
            return true;
        }

        if (this.calibrationSamples.length >= this.minCalibrationSamples) {
            const averaged = this.averageLandmarks(this.calibrationSamples);
            this.personalizedSkeleton = this.buildPersonalizedTargetSkeleton(averaged);
            this.calibrationState = 'ready';
            this.calibrationProgress = 100;
            this.adaptiveMessage = `정상 자세 가이드 생성 완료: ${this.calibrationSamples.length}개 프레임 평균 · reference 팔 굴곡 적용`;
            this.updatePrimarySummary(this.analysis);
            return false;
        }

        this.calibrationStartedAt = Date.now();
        this.calibrationProgress = 0;
        this.calibrationSamples = [];
        this.personalizedSkeleton = [];
        this.adaptiveMessage = '기준 관절이 부족합니다. 어깨, 골반, 양팔이 화면 안에 보이도록 3초 더 맞춰 주세요.';
        this.displayStatus = '캘리브레이션';
        return true;
    }

    private prepareNormalReferenceSkeleton(landmarks: any[]) {
        if (!this.hasCalibrationJoints(landmarks)) {
            this.personalizedSkeleton = [];
            if (this.normalSkeletonEnabled) {
                this.adaptiveMessage = '정상 자세 가이드를 만들 어깨·몸통 관절이 부족합니다.';
            }
            return;
        }
        // Use the detected torso only for one-time body scale/anchor setup.
        // The guide geometry itself comes from the learned normal-pose
        // shoulder/arm reference and is never copied from the current pose.
        this.personalizedSkeleton = this.buildPersonalizedTargetSkeleton(landmarks);
        if (this.normalSkeletonEnabled) {
            this.adaptiveMessage = '학습된 정상 자세 형상을 현재 체형 크기로 생성해 고정했습니다. 현재 관절 모습은 복사하지 않습니다.';
        }
    }

    private hasCalibrationJoints(landmarks: any[]) {
        return [11, 12, 23, 24].every((idx) => this.isVisible(landmarks?.[idx]));
    }

    private isVisible(point: any) {
        return Boolean(point) && (point.visibility ?? 1) >= 0.3 && Number.isFinite(point.x) && Number.isFinite(point.y);
    }

    private cloneLandmarks(landmarks: any[]) {
        return (landmarks || []).map((point: any, index: number) => ({
            id: point?.id ?? index,
            x: Number(point?.x ?? 0),
            y: Number(point?.y ?? 0),
            visibility: Number(point?.visibility ?? 0),
        }));
    }

    private averageLandmarks(samples: any[][]) {
        const count = Math.max(...samples.map((sample) => sample.length), 0);
        const averaged: any[] = [];
        for (let idx = 0; idx < count; idx += 1) {
            const visible = samples
                .map((sample) => sample[idx])
                .filter((point) => this.isVisible(point));
            if (!visible.length) {
                averaged[idx] = { id: idx, x: 0, y: 0, visibility: 0 };
                continue;
            }
            averaged[idx] = {
                id: idx,
                x: visible.reduce((sum, point) => sum + point.x, 0) / visible.length,
                y: visible.reduce((sum, point) => sum + point.y, 0) / visible.length,
                visibility: visible.reduce((sum, point) => sum + point.visibility, 0) / visible.length,
            };
        }
        return averaged;
    }

    private buildPersonalizedTargetSkeleton(source: any[]) {
        const target = this.cloneLandmarks(source);
        const leftShoulder = source[11];
        const rightShoulder = source[12];
        const leftHip = source[23];
        const rightHip = source[24];
        if (!this.isVisible(leftShoulder) || !this.isVisible(rightShoulder) || !this.isVisible(leftHip) || !this.isVisible(rightHip)) {
            return target;
        }

        const shoulderWidth = Math.max(0.04, this.distance(leftShoulder, rightShoulder));
        const hipWidth = Math.max(0.03, this.distance(leftHip, rightHip));
        const shoulderCenter = this.midpoint(leftShoulder, rightShoulder);
        const hipCenter = this.midpoint(leftHip, rightHip);
        const hipY = (leftHip.y + rightHip.y) / 2;
        const leftDirection = leftShoulder.x <= rightShoulder.x ? -1 : 1;
        const rightDirection = -leftDirection;

        const targetShoulderCenter = { x: hipCenter.x, y: shoulderCenter.y };
        target[11] = this.point(11, targetShoulderCenter.x + leftDirection * shoulderWidth / 2, targetShoulderCenter.y, 1);
        target[12] = this.point(12, targetShoulderCenter.x + rightDirection * shoulderWidth / 2, targetShoulderCenter.y, 1);
        target[23] = this.point(23, hipCenter.x + leftDirection * hipWidth / 2, hipY, 1);
        target[24] = this.point(24, hipCenter.x + rightDirection * hipWidth / 2, hipY, 1);

        const targetShoulderWidth = Math.max(0.04, this.distance(target[11], target[12]));
        this.setTargetArm(target, source, 11, 13, 15, leftDirection, targetShoulderWidth, 'left');
        this.setTargetArm(target, source, 12, 14, 16, rightDirection, targetShoulderWidth, 'right');

        return target.map((point) => point ? {
            ...point,
            x: this.clamp(point.x, 0.02, 0.98),
            y: this.clamp(point.y, 0.02, 0.98),
        } : point);
    }

    private setTargetArm(target: any[], source: any[], shoulderIdx: number, elbowIdx: number, wristIdx: number, direction: number, shoulderWidth: number, side: 'left' | 'right') {
        const shoulder = target[shoulderIdx];
        if (!this.isVisible(shoulder)) return;

        const upperLength = this.segmentLength(source, shoulderIdx, elbowIdx, Math.max(0.10, shoulderWidth * 0.48));
        const lowerLength = this.segmentLength(source, elbowIdx, wristIdx, Math.max(0.09, upperLength * 0.86));
        const targetAngle = this.referenceElbowAngle();
        const upperOutRatio = Math.max(this.referenceElbowOutwardRatio(side), this.referenceArmpitGapRatio(side));
        const lowerInRatio = this.referenceWristInwardRatio(side) || this.lowerInwardRatioForAngle(targetAngle, upperOutRatio);
        const elbowOutMagnitude = Math.min(shoulderWidth * upperOutRatio, upperLength * 0.82);
        const elbowOut = direction * elbowOutMagnitude;
        const elbowDown = Math.sqrt(Math.max(upperLength * upperLength - elbowOut * elbowOut, upperLength * upperLength * 0.62));
        const wristReturnMagnitude = Math.min(shoulderWidth * lowerInRatio, elbowOutMagnitude * 0.72, lowerLength * 0.55);
        const wristIn = -direction * wristReturnMagnitude;
        const wristDown = Math.sqrt(Math.max(lowerLength * lowerLength - wristIn * wristIn, lowerLength * lowerLength * 0.62));

        const elbow = this.point(elbowIdx, shoulder.x + elbowOut, shoulder.y + elbowDown, 1);
        const wrist = this.point(wristIdx, elbow.x + wristIn, elbow.y + wristDown, 1);
        target[elbowIdx] = elbow;
        target[wristIdx] = wrist;
    }

    private referenceElbowAngle() {
        const value = Number(this.status?.reference_arm_shape?.target_elbow_angle);
        if (Number.isFinite(value)) return this.clamp(value, 115, 155);
        return 135;
    }

    private referenceElbowOutwardRatio(side?: 'left' | 'right') {
        const sideValue = side ? Number(this.status?.reference_arm_shape?.[`${side}_elbow_outward_offset_mean`]) : NaN;
        const value = Number.isFinite(sideValue) ? sideValue : Number(this.status?.reference_arm_shape?.elbow_outward_offset_mean);
        if (Number.isFinite(value)) return this.clamp(value, 0.18, 0.75);
        return 0.44;
    }

    private referenceArmpitGapRatio(side?: 'left' | 'right') {
        const value = side ? Number(this.status?.reference_arm_shape?.[`${side}_armpit_gap_mean`]) : NaN;
        if (Number.isFinite(value)) return this.clamp(value, 0.12, 0.75);
        return 0.18;
    }

    private referenceWristInwardRatio(side?: 'left' | 'right') {
        const sideValue = side ? Number(this.status?.reference_arm_shape?.[`${side}_wrist_inward_return_mean`]) : NaN;
        const value = Number.isFinite(sideValue) ? sideValue : Number(this.status?.reference_arm_shape?.wrist_inward_return_amount_mean);
        if (Number.isFinite(value)) return this.clamp(value, 0.02, 0.42);
        return 0;
    }

    private lowerInwardRatioForAngle(angleDeg: number, upperOutRatio: number) {
        const targetCos = Math.cos((this.clamp(angleDeg, 115, 155) * Math.PI) / 180);
        const upperDownRatio = Math.sqrt(Math.max(0.01, 1 - upperOutRatio * upperOutRatio));
        let best = 0.42;
        let bestError = Number.POSITIVE_INFINITY;
        for (let ratio = 0.18; ratio <= 0.56; ratio += 0.01) {
            const lowerDownRatio = Math.sqrt(Math.max(0.01, 1 - ratio * ratio));
            const cosValue = upperOutRatio * ratio - upperDownRatio * lowerDownRatio;
            const error = Math.abs(cosValue - targetCos);
            if (error < bestError) {
                best = ratio;
                bestError = error;
            }
        }
        return best;
    }

    private updateArmOverlayDebug(currentLandmarks: any[], data: any) {
        const current = this.armMetrics(currentLandmarks);
        const target = this.armMetrics(this.personalizedSkeleton);
        const apiRows = data?.arm_overlay_debug?.rows || [];
        const apiByName = new Map(apiRows.map((row: any) => [row.name, row]));
        const rows = [
            this.armDebugRow('left_elbow_outward_offset', '왼쪽 팔꿈치 바깥', current, target, apiByName),
            this.armDebugRow('right_elbow_outward_offset', '오른쪽 팔꿈치 바깥', current, target, apiByName),
            this.armDebugRow('left_armpit_gap', '왼쪽 겨드랑이 gap', current, target, apiByName),
            this.armDebugRow('right_armpit_gap', '오른쪽 겨드랑이 gap', current, target, apiByName),
            this.armDebugRow('left_wrist_inward_return', '왼쪽 손목 복귀', current, target, apiByName),
            this.armDebugRow('right_wrist_inward_return', '오른쪽 손목 복귀', current, target, apiByName),
        ].filter(Boolean);
        this.armOverlayDebugRows = rows;
    }

    private armDebugRow(name: string, label: string, current: any, target: any, apiByName: Map<any, any>) {
        const api = apiByName.get(name) || {};
        const ref = this.referenceArmMetric(name);
        const currentValue = Number.isFinite(current?.[name]) ? current[name] : api.current;
        if (!Number.isFinite(currentValue) && !Number.isFinite(target?.[name])) return null;
        const targetValue = Number.isFinite(target?.[name]) ? target[name] : null;
        const std = Number.isFinite(ref.std) ? ref.std : Number(api.std || 0);
        const reference = Number.isFinite(ref.mean) ? ref.mean : Number(api.reference);
        const z = Number.isFinite(currentValue) && Number.isFinite(reference) && std > 1e-9
            ? Math.abs(currentValue - reference) / std
            : Number(api.z_score || 0);
        return {
            name,
            label,
            current: this.formatMetric(currentValue),
            reference: this.formatMetric(reference),
            target: this.formatMetric(targetValue),
            z_score: this.formatMetric(z),
        };
    }

    private referenceArmMetric(name: string) {
        const shape = this.status?.reference_arm_shape || {};
        const mean = Number(shape?.[`${name}_mean`]);
        const std = Number(shape?.[`${name}_std`]);
        return {
            mean: Number.isFinite(mean) ? mean : NaN,
            std: Number.isFinite(std) ? std : NaN,
        };
    }

    private armMetrics(landmarks: any[]) {
        const result: any = {};
        if (!landmarks?.length || !this.isVisible(landmarks[11]) || !this.isVisible(landmarks[12])) return result;
        const shoulderWidth = Math.max(0.04, this.distance(landmarks[11], landmarks[12]));
        if (this.isVisible(landmarks[13])) {
            result.left_elbow_outward_offset = Math.max(0, (landmarks[11].x - landmarks[13].x) / shoulderWidth);
            if (this.isVisible(landmarks[15])) {
                result.left_wrist_inward_return = Math.max(0, (landmarks[15].x - landmarks[13].x) / shoulderWidth);
            }
            if (this.isVisible(landmarks[23])) {
                result.left_armpit_gap = this.pointLineDistance(landmarks[13], landmarks[11], landmarks[23]) / shoulderWidth;
            }
        }
        if (this.isVisible(landmarks[14])) {
            result.right_elbow_outward_offset = Math.max(0, (landmarks[14].x - landmarks[12].x) / shoulderWidth);
            if (this.isVisible(landmarks[16])) {
                result.right_wrist_inward_return = Math.max(0, (landmarks[14].x - landmarks[16].x) / shoulderWidth);
            }
            if (this.isVisible(landmarks[24])) {
                result.right_armpit_gap = this.pointLineDistance(landmarks[14], landmarks[12], landmarks[24]) / shoulderWidth;
            }
        }
        return result;
    }

    private pointLineDistance(point: any, lineA: any, lineB: any) {
        const vx = lineB.x - lineA.x;
        const vy = lineB.y - lineA.y;
        const wx = point.x - lineA.x;
        const wy = point.y - lineA.y;
        const denom = Math.sqrt(vx * vx + vy * vy);
        if (denom < 1e-6) return this.distance(point, lineA);
        return Math.abs(vx * wy - vy * wx) / denom;
    }

    private formatMetric(value: any) {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? Number(numeric.toFixed(3)) : '-';
    }

    private segmentLength(source: any[], a: number, b: number, fallback: number) {
        if (this.isVisible(source[a]) && this.isVisible(source[b])) {
            return Math.max(0.04, this.distance(source[a], source[b]));
        }
        return fallback;
    }

    private midpoint(a: any, b: any) {
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }

    private distance(a: any, b: any) {
        const dx = (a?.x ?? 0) - (b?.x ?? 0);
        const dy = (a?.y ?? 0) - (b?.y ?? 0);
        return Math.sqrt(dx * dx + dy * dy);
    }

    private point(id: number, x: number, y: number, visibility: number) {
        return { id, x, y, visibility };
    }

    private clamp(value: number, min: number, max: number) {
        return Math.min(max, Math.max(min, value));
    }
}

export default PageXrayComponent;